/* =========================================================================
   MediCarry - Shopify theme interactions
   ========================================================================= */

const FREE_SHIPPING_THRESHOLDS = { GBP: 40, EUR: 50, USD: 55, NOK: 540, SEK: 540, DKK: 375, CHF: 45, CAD: 75 };
let FREE_SHIPPING_THRESHOLD = 40;

// The free MediCarry Care Kit is added at £0, so Shopify's Ajax cart never
// exposes its compare-at price. Mirror the per-currency Care Kit value shown on
// the PDP (in cents) so the cart can strike it through. Keep in sync with the
// care_kit_cents block in snippets/pdp-bogo-bundle.liquid.
const CARE_KIT_PRODUCT_ID = 16280472060249;
const CARE_KIT_VALUES = { GBP: 500, USD: 800, EUR: 600, CAD: 1000, AUD: 1000, NZD: 1200, NOK: 6400, SEK: 6400, DKK: 4400, CHF: 450 };
const URGENCY_KEY = 'heron.urgency.start';
const URGENCY_DURATION_MS = 10 * 60 * 1000;
const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

// ---------- Currency formatting (uses Shopify's localized format) ----------

// Seed from Shopify's active presentment currency so market-currency formatting
// (fmt) is correct on first paint, before any /cart.js fetch runs. fetchCart()
// later refreshes it from the live cart.
let CART_CURRENCY = (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) || 'GBP';

function fmt(pence) {
  const value = pence / 100;
  const isWhole = value === Math.floor(value);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: CART_CURRENCY,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: isWhole ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch (e) {
    return isWhole ? `£${Math.floor(value)}` : `£${value.toFixed(2)}`;
  }
}

// Parse a money string shown on the page (e.g. "€12", "kr 135", "$58.50",
// "€58,50", "CHF 10") back into integer cents. Handles either decimal separator.
function parsePriceToCents(text) {
  if (!text) return 0;
  const cleaned = String(text).replace(/[^0-9.,]/g, '');
  if (!cleaned) return 0;
  const decPos = Math.max(cleaned.lastIndexOf(','), cleaned.lastIndexOf('.'));
  let normalized;
  if (decPos !== -1 && cleaned.length - decPos - 1 <= 2) {
    normalized = cleaned.slice(0, decPos).replace(/[.,]/g, '') + '.' + cleaned.slice(decPos + 1).replace(/[.,]/g, '');
  } else {
    normalized = cleaned.replace(/[.,]/g, '');
  }
  const val = parseFloat(normalized);
  return isNaN(val) ? 0 : Math.round(val * 100);
}

const state = {
  bundle: 1,
  variantId: null,
  variantPrice: 0,
};
window.HERON_STATE = state;

const labels = {
  1: { name: 'Single', sub: '1 bracelet' },
  2: { name: 'Wear + spare', sub: '2 bracelets' },
  3: { name: 'The set', sub: '3 bracelets' },
};

// ---------- Cart fetching ----------

async function fetchCart() {
  const res = await fetch('/cart.js', { credentials: 'same-origin' });
  const cart = await res.json();
  if (cart.currency) {
    CART_CURRENCY = cart.currency;
    FREE_SHIPPING_THRESHOLD = FREE_SHIPPING_THRESHOLDS[cart.currency] || 40;
  }
  return cart;
}

// Backend-only source tags. Keys are '_'-prefixed, so Shopify hides them from the
// customer in the cart, checkout, and emails, but they ARE stored on the order
// line item and are queryable via the Admin API. This lets us later audit WHERE
// each add-on was added (`_added_at`: cart vs product-page) and WHAT was added
// (`_added_via`: the specific placement) — WITHOUT touching pricing, discounts
// (BOGO matches by product/quantity, not by properties), or the cart drawer's
// by-variant display grouping. Purely additive metadata; safe to remove anytime.
function addSourceProps(via, at) {
  return { _added_at: at, _added_via: via };
}

async function addToCart(variantId, quantity, properties) {
  const payload = { id: variantId, quantity };
  if (properties) payload.properties = properties;
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json();
}

async function updateLineQty(line, quantity) {
  const res = await fetch('/cart/change.js', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ line, quantity }),
  });
  return res.json();
}

// Update the header cart-count badge from an already-fetched cart object (no network).
function setCartCount(cart) {
  const badge = $('#cartCount');
  if (!badge) return;
  badge.textContent = String(cart.item_count);
  badge.classList.toggle('hidden', cart.item_count === 0);
}

async function updateCartCount() {
  setCartCount(await fetchCart());
}

// ---------- Mobile menu ----------

const mobileMenuBtn = $('#mobileMenuBtn');
const mobileMenu = $('#mobileMenu');
if (mobileMenuBtn && mobileMenu) {
  // The header sits inside .site-topbar, which uses transform / will-change /
  // backdrop-filter — any of those traps a position:fixed overlay inside that box.
  // Re-parent the menu to <body> so the full-page overlay covers the viewport.
  if (mobileMenu.parentElement !== document.body) document.body.appendChild(mobileMenu);
  const iconOpen = mobileMenuBtn.querySelector('.menu-icon-open');
  const iconClose = mobileMenuBtn.querySelector('.menu-icon-close');
  const setMenuOpen = (open) => {
    mobileMenu.classList.toggle('open', open);
    mobileMenuBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    mobileMenuBtn.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    mobileMenu.setAttribute('aria-hidden', open ? 'false' : 'true');
    if (iconOpen) iconOpen.classList.toggle('hidden', open);
    if (iconClose) iconClose.classList.toggle('hidden', !open);
    document.body.classList.toggle('menu-open', open);
    mobileMenu.classList.remove('show-sub'); // always start/leave on the main panel
  };
  mobileMenuBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    setMenuOpen(!mobileMenu.classList.contains('open'));
  });
  mobileMenu.querySelectorAll('a').forEach((a) => {
    a.addEventListener('click', () => setMenuOpen(false));
  });
  mobileMenu.querySelectorAll('[data-menu-close]').forEach((b) => {
    b.addEventListener('click', () => setMenuOpen(false));
  });
  const showSub = (name) => {
    mobileMenu.querySelectorAll('.mm-panel--sub').forEach((p) => {
      p.classList.toggle('is-active', !!name && p.dataset.sub === name);
    });
    mobileMenu.classList.toggle('show-sub', !!name);
  };
  mobileMenu.querySelectorAll('[data-open-sub]').forEach((b) => {
    b.addEventListener('click', () => showSub(b.dataset.openSub));
  });
  mobileMenu.querySelectorAll('[data-close-sub]').forEach((b) => {
    b.addEventListener('click', () => showSub(null));
  });
  document.addEventListener('click', (e) => {
    if (!mobileMenu.classList.contains('open')) return;
    if (mobileMenu.contains(e.target) || mobileMenuBtn.contains(e.target)) return;
    setMenuOpen(false);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && mobileMenu.classList.contains('open')) setMenuOpen(false);
  });
}

// ---------- Bundle picker ----------

function selectBundle(card) {
  $$('.bundle-card').forEach((c) => {
    c.classList.remove('active');
    c.setAttribute('aria-checked', 'false');
    c.style.boxShadow = '';
  });
  card.classList.add('active');
  card.setAttribute('aria-checked', 'true');
  card.style.boxShadow = '0 0 0 1.5px var(--ink)';
  const rawBundle = card.dataset.bundle;
  state.completeCoverage = rawBundle === 'complete';
  // Complete Coverage is keychain (qty 1) + bracelet (qty 1); the per-pack slot
  // logic treats it as a single main item so it doesn't try to multiply variants.
  state.bundle = state.completeCoverage ? 1 : parseInt(rawBundle, 10);

  // Display the formatted price baked into the card by Liquid (already in shop currency)
  const display = card.dataset.displayPrice || '';
  const mainPrice = $('#mainCtaPrice');
  if (mainPrice && display) mainPrice.textContent = display;
  const floatPrice = $('#floatingPrice');
  if (floatPrice && display) floatPrice.textContent = display;

  if (state.completeCoverage) {
    // Keychain colour/type comes from the hero selectors; the bracelet colour is
    // picked inside the card.
    renderBundleSlots();
    renderCompleteCoverage(card);
  } else {
    clearCompleteCoverage();
    // Render the per-slot colour picker for 2-pack / 3-pack
    renderBundleSlots();
  }
}

// ---------- Per-slot colour picker for bundles >1 ----------

function getSwatchBg(value) {
  const v = String(value || '').toLowerCase();
  if (v.includes('blue')) return 'linear-gradient(135deg, #0a0a0a 50%, #1d4ed8 50%)';
  if (v.includes('silver')) return 'linear-gradient(135deg, #8f8f8f, #d0d0d0 50%, #707070)';
  if (v.includes('gold')) return 'linear-gradient(135deg, #b8842f, #f6e2b0 50%, #cc9a44)';
  if (v.includes('red')) return 'linear-gradient(135deg, #0a0a0a 50%, #b91c1c 50%)';
  if (v.includes('green')) return 'linear-gradient(135deg, #0a0a0a 50%, #15803d 50%)';
  return '#0a0a0a';
}

function renderBundleSlots() {
  const parentSel = document.getElementById('parentColourSelector');
  const parentSwatches = document.getElementById('parentColourSwatches');

  // Hide & empty all slot containers first
  $$('[data-bundle-slots]').forEach(el => {
    el.hidden = true;
    el.innerHTML = '';
  });

  const bundle = state.bundle || 1;
  if (bundle <= 1) {
    if (parentSel) parentSel.hidden = false;
    if (parentSwatches) parentSwatches.hidden = false;
    state.slotVariants = [];
    updateMainCtaText();
    return;
  }

  const activeCard = document.querySelector('.bundle-card.active');
  const list = activeCard?.querySelector('[data-bundle-slots]');
  if (!list) return;

  const product = getProduct();
  if (!product) return;

  const optionNames = (product.options || []).map(o =>
    typeof o === 'string' ? o : (o && o.name ? o.name : '')
  );
  const colourIdx = optionNames.findIndex(n => /colou?r/i.test(n));
  if (colourIdx === -1) return;

  const colourValues = Array.from(new Set(
    (product.variants || []).map(v => v.options[colourIdx]).filter(Boolean)
  ));
  if (colourValues.length === 0) return;

  // Keep the parent Colour selector visible — users use it to preview gallery images

  if (!Array.isArray(state.slotColours)) state.slotColours = [];
  const parentColour = parentSwatches?.querySelector('.colour-swatch.active')?.dataset.colour || colourValues[0];
  while (state.slotColours.length < bundle) state.slotColours.push(parentColour);
  state.slotColours = state.slotColours.slice(0, bundle);

  list.innerHTML = '';

  for (let i = 0; i < bundle; i++) {
    const slotIdx = i + 1;
    const currentColour = state.slotColours[i];

    const slot = document.createElement('div');
    slot.className = 'bundle-slot';
    slot.dataset.slot = slotIdx;

    const label = document.createElement('span');
    label.className = 'bundle-slot__label';
    label.textContent = `Bracelet ${slotIdx}`;
    slot.appendChild(label);

    const swatches = document.createElement('div');
    swatches.className = 'bundle-slot__swatches';
    colourValues.forEach(colour => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'colour-swatch' + (colour === currentColour ? ' active' : '');
      btn.dataset.colour = colour;
      btn.setAttribute('aria-label', colour);
      const inner = document.createElement('span');
      inner.className = 'swatch';
      inner.style.background = getSwatchBg(colour);
      btn.appendChild(inner);
      btn.addEventListener('click', (e) => {
        e.stopPropagation(); // don't bubble to parent bundle card
        state.slotColours[i] = colour;
        slot.querySelectorAll('.colour-swatch').forEach(s => s.classList.toggle('active', s === btn));
        const nameEl = slot.querySelector('.bundle-slot__name');
        if (nameEl) nameEl.textContent = colour;
        syncSlotVariants();
        updateMainCtaText();
      });
      swatches.appendChild(btn);
    });
    slot.appendChild(swatches);

    const name = document.createElement('span');
    name.className = 'bundle-slot__name';
    name.textContent = currentColour;
    slot.appendChild(name);

    list.appendChild(slot);
  }

  list.hidden = false;
  syncSlotVariants();
  updateMainCtaText();
}

function syncSlotVariants() {
  const product = getProduct();
  if (!product || !Array.isArray(state.slotColours)) {
    state.slotVariants = [];
    return;
  }
  const optionNames = (product.options || []).map(o =>
    typeof o === 'string' ? o : (o && o.name ? o.name : '')
  );
  const typeIdx = optionNames.findIndex(n => /^type/i.test(n));
  const colourIdx = optionNames.findIndex(n => /colou?r/i.test(n));
  const activeType = $('.type-pill.active')?.dataset.type;

  state.slotVariants = state.slotColours.map(colour => {
    const variant = (product.variants || []).find(v => {
      if (colourIdx !== -1 && String(v.options[colourIdx]).toLowerCase() !== String(colour).toLowerCase()) return false;
      if (typeIdx !== -1 && activeType && String(v.options[typeIdx]).toLowerCase() !== String(activeType).toLowerCase()) return false;
      return true;
    });
    return variant ? variant.id : null;
  });
}

function getSlotsTotalCents() {
  const product = getProduct();
  if (!product || !Array.isArray(state.slotVariants)) return 0;
  return state.slotVariants.reduce((sum, id) => {
    const v = product.variants.find(x => x.id === id);
    return sum + (v ? v.price : 0);
  }, 0);
}

// ---------- Complete Coverage (keychain + matching bracelet) ----------

const _coverageProducts = {}; // handle -> product json (ONLY successful loads are cached)
const COV_FAILED = Symbol('coverage-load-failed'); // transient failure sentinel

// Fetch one family product's JSON. Returns the product on success (cached), null
// for a genuine 404 (skip permanently), or COV_FAILED for a transient failure
// (network blip, storefront serving the password/HTML page, 429/5xx, empty body).
// Retries are driven by the caller so a partial family load is retried too — the
// bug that made designs go missing intermittently.
async function loadCoverageBracelet(handle) {
  if (!handle) return null;
  if (_coverageProducts[handle]) return _coverageProducts[handle];
  try {
    const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
    if (res.ok) {
      let json = null;
      try { json = await res.json(); } catch (e) { return COV_FAILED; } // HTML/password page
      if (json && Array.isArray(json.variants) && json.variants.length) {
        _coverageProducts[handle] = json;
        return json;
      }
      return COV_FAILED;
    }
    if (res.status === 404) return null; // genuinely missing — don't hammer it
    return COV_FAILED; // 429 / 5xx / other
  } catch (e) {
    return COV_FAILED; // network error
  }
}

// Leaving the tier only HIDES the built dropdowns; it must not wipe them, or
// re-selecting the tier would show empty slots (coverageBuilt still '1').
function clearCompleteCoverage() {
  $$('[data-complete-slots]').forEach(el => { el.hidden = true; });
}

// Open the shared PDP image lightbox on a picker thumbnail (matches bracelet pages).
function openCoverageImage(src) {
  if (!src) return;
  // Plain CSS overlay appended to <body> (proven to work on the live PDP — no
  // transform trap there, and no <dialog> browser-quirk risk). Load a right-sized
  // image, not the multi-MB master, so it appears instantly on mobile data.
  let modal = document.getElementById('coverageLightbox');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'coverageLightbox';
    modal.className = 'cov-lightbox';
    modal.innerHTML = '<div class="cov-lightbox__overlay" data-cov-close></div>' +
      '<div class="cov-lightbox__content"><button type="button" class="cov-lightbox__close" data-cov-close aria-label="Close"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="6" y1="18" x2="18" y2="6"/></svg></button>' +
      '<img class="cov-lightbox__img" alt="" /></div>';
    document.body.appendChild(modal);
    modal.querySelectorAll('[data-cov-close]').forEach(el => el.addEventListener('click', () => {
      modal.dataset.open = 'false'; document.body.style.overflow = '';
    }));
  }
  // Match the add-ons image preview exactly: one sized image, shown directly (no
  // small→large step). The press-preload below warms this same URL so it's fast.
  modal.querySelector('.cov-lightbox__img').src = covThumb(src, 1400);
  modal.dataset.open = 'true';
  document.body.style.overflow = 'hidden';
}

// Build the dropdown option list for a whole family of handles: every design's
// every variant, deduped by image (matches the bracelet PDP bundle picker). Two
// distinct-looking designs both appear; only identical images (e.g. size clones
// of the same design) collapse to one entry.
// Every option in a picker is the SAME condition (it's that condition's keychain
// page), so the condition name repeats on every card ("… Diabetes", "… Blood
// Thinner") and just eats space. Drop the words shared by EVERY label's tail, so
// only what differs remains: "Black & Blue · Type 1 Diabetes" -> "Black & Blue ·
// Type 1", "Silver · Blood Thinner" -> "Silver", "Type 2 Diabetes" -> "Type 2".
// Suffix-only (never strips a shared leading word like "Type"); always keeps >=1 word.
function trimCommonSuffixWords(options) {
  if (!options || options.length < 2) return options;
  const norm = (s) => String(s).toLowerCase();
  const lists = options.map((o) => String(o.label).split(/\s+/).filter(Boolean));
  const minLen = Math.min.apply(null, lists.map((l) => l.length));
  let suf = 0;
  while (suf < minLen - 1 && lists.every((l) => norm(l[l.length - 1 - suf]) === norm(lists[0][lists[0].length - 1 - suf]))) suf++;
  if (!suf) return options;
  return options.map((o) => {
    const words = String(o.label).split(/\s+/).filter(Boolean);
    if (words.length <= suf) return o;
    const mid = words.slice(0, words.length - suf).join(' ').replace(/[·\-\/\s]+$/, '').trim();
    return Object.assign({}, o, { label: mid || o.label });
  });
}

// The product-JSON image URLs are full-resolution masters; rendered in a ~104px
// tile they're a big, slow download. Append Shopify's width= param for a small,
// fast thumbnail (the full URL is kept for the lightbox).
function covThumb(url, w) {
  if (!url) return url;
  return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=' + w;
}

async function coverageFamilyOptions(handles) {
  const seen = new Set();
  const out = [];
  let failed = 0;
  // Fetch the whole family in parallel (serial awaits made the pickers slow to fill).
  const products = await Promise.all(handles.map(loadCoverageBracelet));
  for (const p of products) {
    if (p === COV_FAILED) { failed++; continue; }
    if (!p || !(p.variants || []).length) continue;
    const h = p.handle || '';
    const firstImg = (p.images && p.images[0])
      ? (typeof p.images[0] === 'string' ? p.images[0] : p.images[0].src) : (p.featured_image || '');
    for (const v of p.variants) {
      const img = (v.featured_image && v.featured_image.src) || firstImg || '';
      const raw = (v.title && v.title !== 'Default Title') ? v.title : (p.title || '');
      const label = String(raw).replace(/\s*\/\s*/g, ' · ');
      const key = (img || '').split('?')[0] || (h + '::' + label.toLowerCase());
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ id: v.id, label, image: img, thumb: covThumb(img, 240), handle: h });
    }
  }
  return { options: trimCommonSuffixWords(out), failed };
}

function coverageHandleList(str) {
  return String(str || '').split(',').map(s => s.trim()).filter(Boolean);
}

// Build a horizontal card carousel (image + "i" button + name) for a whole family,
// with prev/next arrows that scroll left/right — the same pattern as the PDP
// cross-sell and main-image galleries. `role` is 'keychain' or 'bracelet'. The
// track carries its own choice on track.dataset.selectedId; it never writes global
// state directly (so warming the pickers on load can't clobber the single/pair
// tiers' variant). State is applied via applyCoverageState() when the Complete
// Coverage tier is active. The whole card selects a design; the "i" button opens
// the image lightbox. update() is stashed on the track so it can be recomputed
// when the tier is first revealed (the slots are display:none until then).
function buildCoverageCarousel(labelText, options, role) {
  const wrap = document.createElement('div');
  wrap.className = 'cov-field';

  const lab = document.createElement('p');
  lab.className = 'cov-field__label';
  lab.textContent = labelText;
  wrap.appendChild(lab);

  const carousel = document.createElement('div');
  carousel.className = 'cov-carousel';

  const track = document.createElement('div');
  track.className = 'cov-carousel__track';
  track.dataset.covRole = role;

  let selected = options[0];
  track.dataset.selectedId = selected.id;
  track.dataset.selectedHandle = selected.handle || '';

  const esc = (s) => String(s).replace(/</g, '&lt;');
  const cardHtml = (o) => `<button type="button" class="cov-card${o.id === selected.id ? ' is-sel' : ''}" data-id="${o.id}" aria-pressed="${o.id === selected.id}">
      <span class="cov-card__info" data-cov-info aria-hidden="true">i</span>
      <span class="cov-card__media">${o.image ? `<img src="${o.thumb || o.image}" alt="" width="240" height="240" loading="eager" fetchpriority="low" decoding="async" />` : ''}</span>
      <span class="cov-card__name">${esc(o.label)}</span>
    </button>`;
  track.innerHTML = options.map(cardHtml).join('');

  const arrow = (dir, cls, points) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'gallery-arrow ' + cls + ' cov-carousel__arrow';
    b.setAttribute('aria-label', dir);
    b.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="${points}"/></svg>`;
    return b;
  };
  const prevBtn = arrow('Previous', 'gallery-arrow--prev', '15,18 9,12 15,6');
  const nextBtn = arrow('Next', 'gallery-arrow--next', '9,18 15,12 9,6');

  carousel.appendChild(prevBtn);
  carousel.appendChild(track);
  carousel.appendChild(nextBtn);

  function cardStep() {
    const c = track.querySelector('.cov-card');
    if (!c) return track.clientWidth;
    const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 8;
    return c.getBoundingClientRect().width + gap;
  }
  function update() {
    // Use the track's actual rendered width (getBoundingClientRect) rather than
    // clientWidth — reliable for a grid track — and only treat it as overflowing
    // once it has a real width (avoids a false positive while it's 0/hidden).
    const visible = track.getBoundingClientRect().width;
    const max = track.scrollWidth - visible;
    const overflows = visible > 0 && max > 4;
    // Only write when it actually changes — avoids redundant style invalidation.
    if (carousel.classList.contains('has-overflow') !== overflows) {
      carousel.classList.toggle('has-overflow', overflows);
    }
    prevBtn.disabled = !overflows || track.scrollLeft <= 4;
    nextBtn.disabled = !overflows || track.scrollLeft >= max - 4;
  }
  track.__covUpdate = update;

  prevBtn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); track.scrollBy({ left: -cardStep(), behavior: 'smooth' }); });
  nextBtn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); track.scrollBy({ left: cardStep(), behavior: 'smooth' }); });
  track.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update);

  // Warm the lightbox image the instant a finger presses "i" (fires ~100-300ms
  // before the click), so the picture is already loading when the modal opens.
  track.addEventListener('pointerdown', (e) => {
    const info = e.target.closest('[data-cov-info]');
    if (!info) return;
    const c = info.closest('.cov-card');
    const o = c && options.find(x => x.id === parseInt(c.dataset.id, 10));
    if (o && o.image) { const im = new Image(); im.src = covThumb(o.image, 1400); }
  }, { passive: true });

  track.addEventListener('click', (e) => {
    // The "i" button opens the image lightbox instead of selecting the design.
    const info = e.target.closest('[data-cov-info]');
    if (info) {
      e.preventDefault(); e.stopPropagation();
      const c = info.closest('.cov-card');
      const o = c && options.find(x => x.id === parseInt(c.dataset.id, 10));
      openCoverageImage(o && o.image);
      return;
    }
    const card = e.target.closest('.cov-card');
    if (!card) return;
    e.preventDefault(); e.stopPropagation();
    const id = parseInt(card.dataset.id, 10);
    selected = options.find(o => o.id === id) || selected;
    track.querySelectorAll('.cov-card').forEach(c => {
      const sel = parseInt(c.dataset.id, 10) === id;
      c.classList.toggle('is-sel', sel);
      c.setAttribute('aria-pressed', sel ? 'true' : 'false');
    });
    track.dataset.selectedId = selected.id;
    track.dataset.selectedHandle = selected.handle || '';
    // Warm the per-market "was" price for this design now, so the cart drawer paints
    // instantly on Add instead of blocking on the section-render probe.
    if (selected.handle) fetchMarketPrices(selected.handle);
    if (state.completeCoverage) { applyCoverageState(); updateMainCtaText(); }
  });

  wrap.appendChild(carousel);
  requestAnimationFrame(update);
  return wrap;
}

// Recompute each carousel's overflow/arrow state. Needed when the Complete Coverage
// tier is first revealed: the pickers are built while [data-complete-slots] is
// display:none (scrollWidth/clientWidth read 0), so arrows must be re-evaluated
// once the slots become visible.
function refreshCoverageCarousels(card) {
  card.querySelectorAll('.cov-carousel__track').forEach(t => {
    if (!t.__covUpdate) return;
    // Measure once, after two frames, so layout has fully settled (the tier just went
    // from display:none to visible). A single settled measurement — not competing
    // early/late ones — so the arrows resolve once with no flicker. update() is
    // guarded, so the resize/scroll listeners only ever re-toggle on a real change.
    requestAnimationFrame(() => requestAnimationFrame(t.__covUpdate));
  });
}

// Read both dropdowns' current choices into dedicated coverage state. Kept separate
// from state.variantId so the single/pair tiers (driven by the hero selectors) are
// never affected by the coverage pickers.
function applyCoverageState() {
  const card = document.querySelector('.bundle-card--coverage');
  if (!card) return;
  card.querySelectorAll('[data-cov-role]').forEach(dd => {
    const id = parseInt(dd.dataset.selectedId, 10);
    if (!id) return;
    if (dd.dataset.covRole === 'keychain') state.coverageKeychainVariant = id;
    else if (dd.dataset.covRole === 'bracelet') state.coverageBraceletVariant = id;
  });
}

// Build the two dropdowns ONCE per card. Idempotent and concurrency-safe: parallel
// callers share one in-flight promise. Returns true when built, false on a load
// failure (so the caller can retry later). A family that has handles but resolves
// to zero options is treated as a failure — better to retry than to render a
// tier missing its bracelet (or keychain) picker.
const _coverageBuilding = new WeakMap();
async function ensureCoverageBuilt(card) {
  const slots = card && card.querySelector('[data-complete-slots]');
  if (!slots) return false;
  if (card.dataset.coverageBuilt === '1') return true;
  if (_coverageBuilding.has(card)) return _coverageBuilding.get(card);

  const build = (async () => {
    const kHandles = coverageHandleList(card.dataset.coverageKeychains);
    const bHandles = coverageHandleList(card.dataset.coverageBracelets);
    const [kRes, bRes] = await Promise.all([
      coverageFamilyOptions(kHandles),
      coverageFamilyOptions(bHandles),
    ]);
    const keychainOpts = kRes.options;
    const braceletOpts = bRes.options;

    // ANY transient failure in either family means the list may be incomplete —
    // bail so the caller retries (successful designs are cached, so a retry only
    // re-fetches the ones that failed). Genuine 404s don't count as failures.
    if (kRes.failed || bRes.failed) return false;
    // An expected family that resolved to zero designs is also a failure.
    if ((kHandles.length && !keychainOpts.length) || (bHandles.length && !braceletOpts.length)) return false;
    if (!keychainOpts.length && !braceletOpts.length) return false;

    slots.innerHTML = '';
    if (keychainOpts.length) {
      slots.appendChild(buildCoverageCarousel('Choose your keychain', keychainOpts, 'keychain'));
    }
    if (braceletOpts.length) {
      slots.appendChild(buildCoverageCarousel('Choose your bracelet', braceletOpts, 'bracelet'));
    }
    card.dataset.coverageBuilt = '1';
    if (state.completeCoverage) applyCoverageState();
    updateMainCtaText();
    return true;
  })();

  _coverageBuilding.set(card, build);
  try { return await build; }
  finally { _coverageBuilding.delete(card); }
}

async function renderCompleteCoverage(card) {
  const slots = card.querySelector('[data-complete-slots]');
  if (!slots) return;
  slots.hidden = false;
  const ok = await ensureCoverageBuilt(card);
  if (ok === true) {
    applyCoverageState(); updateMainCtaText(); refreshCoverageCarousels(card);
    // Warm the "was" price probe for the default keychain + bracelet the moment the
    // tier opens, so Add-to-cart's drawer doesn't stall on the section render.
    card.querySelectorAll('.cov-carousel__track').forEach(t => {
      if (t.dataset.selectedHandle) fetchMarketPrices(t.dataset.selectedHandle);
    });
  }
  else warmCoverage(card, 1); // transient failure — keep retrying with backoff
}

// Retry ensureCoverageBuilt with backoff until it fills (or we give up after a
// handful of attempts). Not tier-gated: warming in the background is harmless and
// means the tier is ready the moment it's opened. Idempotent once built.
const _coverageWarmTimers = new WeakMap();
function warmCoverage(card, attempt) {
  const prev = _coverageWarmTimers.get(card);
  if (prev) clearTimeout(prev);
  if (attempt > 8) {
    // Exhausted retries — show a retry affordance rather than a silent empty tier.
    if (card.dataset.coverageBuilt !== '1') renderCoverageError(card);
    return;
  }
  const t = setTimeout(async () => {
    const ok = await ensureCoverageBuilt(card);
    if (ok === false) warmCoverage(card, attempt + 1);
  }, Math.min(400 * attempt, 3000));
  _coverageWarmTimers.set(card, t);
}

function renderCoverageError(card) {
  const slots = card.querySelector('[data-complete-slots]');
  if (!slots || card.dataset.coverageBuilt === '1') return;
  slots.hidden = false;
  slots.innerHTML = '<div class="coverage-error">' +
    '<span>Couldn\'t load the options. Check your connection.</span>' +
    '<button type="button" class="coverage-error__retry" data-coverage-retry>Retry</button></div>';
  const btn = slots.querySelector('[data-coverage-retry]');
  if (btn) btn.addEventListener('click', (e) => {
    e.preventDefault(); e.stopPropagation();
    slots.innerHTML = '';
    warmCoverage(card, 1);
    ensureCoverageBuilt(card).then(ok => { if (ok === false) warmCoverage(card, 1); });
  });
}

// Warm the dropdowns as soon as the page is idle, regardless of the active tier,
// so switching to Complete Coverage is instant and resilient to a slow first load.
window.addEventListener('DOMContentLoaded', () => {
  const cards = $$('[data-complete-coverage]');
  if (!cards.length) return;
  const warm = () => cards.forEach(async (card) => {
    const ok = await ensureCoverageBuilt(card);
    if (ok === false) warmCoverage(card, 1);
  });
  if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 2500 });
  else setTimeout(warm, 600);
});

$$('.bundle-card').forEach((card) => {
  card.addEventListener('click', () => selectBundle(card));
  // Keyboard accessibility for role="button" divs
  card.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      selectBundle(card);
    }
  });
});

window.addEventListener('DOMContentLoaded', () => {
  const initial = $('.bundle-card.active') || $('.bundle-card[data-bundle="1"]');
  if (initial) selectBundle(initial);
});

// ---------- Variant selection (real Shopify variant resolution) ----------

let _CACHED_PRODUCT = null;
function getProduct() {
  if (_CACHED_PRODUCT) return _CACHED_PRODUCT;
  const el = $('[data-product-json]');
  if (!el) return null;
  try { _CACHED_PRODUCT = JSON.parse(el.textContent); } catch { return null; }
  return _CACHED_PRODUCT;
}

function findMatchingVariant(product, selected) {
  return product.variants.find(v => {
    return product.options.every((opt, i) => {
      const optName = typeof opt === 'string' ? opt : opt.name;
      if (selected[optName] === undefined) return true;
      return v.options[i] === selected[optName];
    });
  });
}

function getSelectedOptions() {
  const selected = {};
  $$('.type-pill.active').forEach(b => selected['Type'] = b.dataset.type);
  $$('.colour-swatch.active').forEach(b => selected['Colour'] = b.dataset.colour);
  return selected;
}

function syncVariant() {
  const product = getProduct();
  if (!product) return;
  const match = findMatchingVariant(product, getSelectedOptions());
  if (!match) return;

  state.variantId = match.id;
  state.variantPrice = match.price;

  // Update URL so refresh keeps the selection
  if (history.replaceState) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', match.id);
    history.replaceState({}, '', url);
  }

  // Jump carousel to the slide for this variant's featured image
  if (match.featured_image && match.featured_image.src) {
    jumpGalleryToVariantImage(match);
  }

  // Update availability state on add-to-cart button
  const addBtn = $('#mainAddToCart');
  const floatBtn = $('#floatingAdd');
  if (!match.available) {
    [addBtn, floatBtn].forEach(b => {
      if (!b) return;
      b.disabled = true;
      b.classList.add('opacity-50', 'cursor-not-allowed');
    });
  } else {
    [addBtn, floatBtn].forEach(b => {
      if (!b) return;
      b.disabled = false;
      b.classList.remove('opacity-50', 'cursor-not-allowed');
    });
  }
}

$$('.type-pill').forEach(btn => {
  btn.addEventListener('click', () => {
    $$('.type-pill').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    const eyebrow = $('#heroEyebrow');
    // The Type value already contains "Diabetes" (e.g. "Type 1 Diabetes"), so the
    // suffix is just the product noun ("Bracelet" / "Slim Bracelet") — never repeat "Diabetes".
    if (eyebrow) eyebrow.textContent = `${btn.dataset.type} ${eyebrow.dataset.eyebrowSuffix || 'Bracelet'}`;
    syncVariant();
    renderPdpCrossSell(); // re-filter the cross-sell to the newly selected Type
  });
});

function updateParentColourLabel() {
  const parent = document.getElementById('parentColourSwatches');
  const label = document.querySelector('[data-parent-colour-label]');
  if (!label) return;
  const active = parent ? parent.querySelector('.colour-swatch.active') : document.querySelector('.colour-swatch.active');
  if (active && active.dataset.colour) label.textContent = active.dataset.colour;
}

$$('.colour-swatch').forEach(swatch => {
  swatch.addEventListener('click', () => {
    $$('.colour-swatch').forEach(s => s.classList.remove('active'));
    swatch.classList.add('active');
    updateParentColourLabel();
    syncVariant();
  });
});

// Sync from a BOGO tier slot: when a picked slot colour is a variant of THIS product
// (bogo.js dispatches 'hero:setColour'), reflect it in the hero colour selector + gallery.
document.addEventListener('hero:setColour', (e) => {
  const colour = e && e.detail && e.detail.colour;
  if (!colour) return;
  const parent = document.getElementById('parentColourSwatches');
  const swatches = parent ? Array.from(parent.querySelectorAll('.colour-swatch')) : $$('.colour-swatch');
  const match = swatches.find(s => s.dataset.colour === colour);
  if (!match) return; // colour isn't on this product page → ignore
  swatches.forEach(s => s.classList.toggle('active', s === match));
  updateParentColourLabel();
  syncVariant();
});

window.addEventListener('DOMContentLoaded', () => {
  // Initialise variant from URL ?variant=ID
  const product = getProduct();
  if (product) {
    const params = new URLSearchParams(window.location.search);
    const variantId = parseInt(params.get('variant'), 10);
    if (variantId) {
      const variant = product.variants.find(v => v.id === variantId);
      if (variant) {
        product.options.forEach((opt, i) => {
          const optName = typeof opt === 'string' ? opt : opt.name;
          const value = variant.options[i];
          if (optName === 'Type') {
            $$('.type-pill').forEach(b => b.classList.toggle('active', b.dataset.type === value));
          } else if (optName === 'Colour') {
            $$('.colour-swatch').forEach(s => s.classList.toggle('active', s.dataset.colour === value));
          }
        });
      }
    }
  }
  updateParentColourLabel();
  syncVariant();
  renderPdpCrossSell(); // render with the Type/variant resolved from the URL
});

// ---------- Gallery thumbnails (click to scroll carousel to slide) ----------

const galleryTrack = $('#galleryTrack');

// Keep the active thumbnail visible within its (horizontally scrollable) strip.
function scrollThumbIntoView(idx) {
  const thumbs = $$('.gallery-thumb');
  const el = thumbs[idx];
  if (!el) return;
  const strip = el.parentElement;
  if (!strip) return;
  const elLeft = el.offsetLeft - strip.offsetLeft;
  const elRight = elLeft + el.offsetWidth;
  const viewLeft = strip.scrollLeft;
  const viewRight = viewLeft + strip.clientWidth;
  if (elLeft < viewLeft) {
    strip.scrollTo({ left: Math.max(0, elLeft - 8), behavior: 'smooth' });
  } else if (elRight > viewRight) {
    strip.scrollTo({ left: elRight - strip.clientWidth + 8, behavior: 'smooth' });
  }
}

$$('.gallery-thumb').forEach(thumb => {
  let pointerDownAt = null;
  let dragged = false;
  thumb.addEventListener('pointerdown', (e) => {
    pointerDownAt = { x: e.clientX, y: e.clientY };
    dragged = false;
  });
  thumb.addEventListener('pointermove', (e) => {
    if (!pointerDownAt) return;
    const dx = Math.abs(e.clientX - pointerDownAt.x);
    const dy = Math.abs(e.clientY - pointerDownAt.y);
    if (dx > 3 || dy > 3) dragged = true;
  });
  thumb.addEventListener('pointercancel', () => { pointerDownAt = null; dragged = false; });
  thumb.addEventListener('click', (e) => {
    const wasDrag = dragged;
    pointerDownAt = null;
    dragged = false;
    if (wasDrag) { e.preventDefault(); return; }
    const slideIdx = parseInt(thumb.dataset.slide, 10);
    const slides = $$('.gallery-slide');
    if (!galleryTrack || !slides[slideIdx]) return;
    galleryTrack.scrollTo({ left: slides[slideIdx].offsetLeft - galleryTrack.offsetLeft, behavior: 'smooth' });
    $$('.gallery-thumb').forEach(t => t.classList.remove('active'));
    thumb.classList.add('active');
    scrollThumbIntoView(slideIdx);
  });
});

// Sync thumb-active state when user swipes the carousel
if (galleryTrack) {
  let scrollTimer;
  galleryTrack.addEventListener('scroll', () => {
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(() => {
      const slides = $$('.gallery-slide');
      const idx = Math.round(galleryTrack.scrollLeft / galleryTrack.clientWidth);
      $$('.gallery-thumb').forEach((t, i) => t.classList.toggle('active', i === idx));
      scrollThumbIntoView(idx);
      updateGalleryArrowState();
    }, 80);
  }, { passive: true });
}

// Best Seller badge: pinned over the first image, fades out once the carousel
// scrolls toward the second image (and fades back in on return to image 1).
if (galleryTrack) {
  const bsBadge = document.querySelector('.gallery-track-wrap .best-seller-badge');
  if (bsBadge) {
    let fadeTimer = null;
    let trackWidth = galleryTrack.clientWidth;
    let rafPending = false;
    const applyBadgeState = () => {
      rafPending = false;
      if (!trackWidth) return;
      const sl = galleryTrack.scrollLeft;
      if (sl >= trackWidth * 0.9) {
        // Second image fully in view: hold half a second, then fade out (once).
        if (!fadeTimer && !bsBadge.classList.contains('is-hidden')) {
          fadeTimer = setTimeout(() => { bsBadge.classList.add('is-hidden'); fadeTimer = null; }, 500);
        }
      } else if (sl <= trackWidth * 0.1) {
        // Back on the first image (settled): cancel any pending fade and fade the badge in.
        if (fadeTimer) { clearTimeout(fadeTimer); fadeTimer = null; }
        bsBadge.classList.remove('is-hidden');
      }
      // Between the two images: keep the current state (no layout thrash mid-scroll).
    };
    // rAF-throttled + cached width so scrolling stays smooth (no per-frame layout reads).
    galleryTrack.addEventListener('scroll', () => {
      if (!rafPending) { rafPending = true; requestAnimationFrame(applyBadgeState); }
    }, { passive: true });
    window.addEventListener('resize', () => { trackWidth = galleryTrack.clientWidth; }, { passive: true });
    applyBadgeState();
  }
}

// Desktop carousel arrow buttons
function scrollGalleryToIndex(idx) {
  if (!galleryTrack) return;
  const slides = $$('.gallery-slide');
  const target = slides[Math.max(0, Math.min(slides.length - 1, idx))];
  if (target) galleryTrack.scrollTo({ left: target.offsetLeft - galleryTrack.offsetLeft, behavior: 'smooth' });
}
function currentGalleryIndex() {
  if (!galleryTrack) return 0;
  return Math.round(galleryTrack.scrollLeft / galleryTrack.clientWidth);
}
function updateGalleryArrowState() {
  if (!galleryTrack) return;
  const slides = $$('.gallery-slide');
  const idx = currentGalleryIndex();
  $$('[data-gallery-prev]').forEach(b => b.toggleAttribute('disabled', idx <= 0));
  $$('[data-gallery-next]').forEach(b => b.toggleAttribute('disabled', idx >= slides.length - 1));
}
$$('[data-gallery-prev]').forEach(btn => btn.addEventListener('click', () => scrollGalleryToIndex(currentGalleryIndex() - 1)));
$$('[data-gallery-next]').forEach(btn => btn.addEventListener('click', () => scrollGalleryToIndex(currentGalleryIndex() + 1)));
updateGalleryArrowState();

// Variant change should jump the carousel to that variant's image
function jumpGalleryToVariantImage(variant) {
  if (!variant.featured_image || !galleryTrack) return;
  const product = getProduct();
  if (!product) return;
  const idx = product.images.findIndex(img => {
    const imgSrc = typeof img === 'string' ? img : img.src;
    return imgSrc && imgSrc.includes(variant.featured_image.src.split('?')[0]);
  });
  if (idx < 0) return;
  const slides = $$('.gallery-slide');
  if (!slides[idx]) return;
  galleryTrack.scrollTo({ left: slides[idx].offsetLeft - galleryTrack.offsetLeft, behavior: 'smooth' });
}

// ---------- Add to cart ----------

async function addCurrentToCart() {
  if (!state.variantId) {
    const product = getProduct();
    if (product) state.variantId = product.variants[0]?.id;
  }
  if (!state.variantId) return;

  let items;
  if (state.completeCoverage) {
    // One chosen keychain + one chosen bracelet (both picked in the tier's dropdowns).
    applyCoverageState();
    const keychainId = state.coverageKeychainVariant || state.variantId;
    const coverProps = addSourceProps('pdp-coverage', 'product-page');
    items = [];
    if (keychainId) items.push({ id: keychainId, quantity: 1, properties: coverProps });
    if (state.coverageBraceletVariant) items.push({ id: state.coverageBraceletVariant, quantity: 1, properties: coverProps });
    if (!items.length && state.variantId) items.push({ id: state.variantId, quantity: 1, properties: coverProps });
  } else if (state.bundle > 1 && Array.isArray(state.slotVariants) && state.slotVariants.length === state.bundle && state.slotVariants.every(Boolean)) {
    // Each slot is its own line item; same-colour duplicates merge into one line at qty N (Shopify behaviour).
    // The identical source tag on every slot preserves that merge.
    const bundleProps = addSourceProps('pdp-buy' + state.bundle, 'product-page');
    items = state.slotVariants.map(id => ({ id, quantity: 1, properties: bundleProps }));
  } else {
    // Customer's own main product (single or plain qty) — left untagged on purpose.
    items = [{ id: state.variantId, quantity: state.bundle }];
  }

  // Add every toggled-on PDP add-on (carousel — independent toggles)
  $$('[data-upsell-card]').forEach((card) => {
    const sw = card.querySelector('[data-upsell-switch]');
    if (sw && sw.getAttribute('aria-checked') === 'true') {
      const vid = parseInt(sw.dataset.currentVariant, 10);
      if (vid) items.push({ id: vid, quantity: 1, properties: addSourceProps('pdp-addon', 'product-page') });
    }
  });

  if (items.length === 1) {
    await addToCart(items[0].id, items[0].quantity, items[0].properties);
  } else {
    await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items }),
    });
  }
  // openDrawer() → renderDrawer() refreshes the header count badge and starts the
  // urgency timer, so no separate updateCartCount()/fetch is needed here.
  openDrawer();
}

$('#mainAddToCart')?.addEventListener('click', addCurrentToCart);
// On BOGO pages the floating button is owned by bogo.js (it must add the active
// tier + chosen slots, not a single plain item), so only wire the plain add here
// when there's no BOGO block on the page.
if (!document.querySelector('[data-bogo]')) {
  $('#floatingAdd')?.addEventListener('click', addCurrentToCart);
}

// ---------- PDP add-on carousel (multiple independent toggle upsells) ----------

const _upsellProducts = {}; // handle -> product json (or null)

async function loadUpsellCardProduct(card) {
  const handle = card.dataset.upsellHandle;
  if (!handle) return null;
  if (_upsellProducts[handle] !== undefined) return _upsellProducts[handle];
  try {
    const res = await fetch(`/products/${handle}.js`);
    _upsellProducts[handle] = res.ok ? await res.json() : null;
  } catch (e) { _upsellProducts[handle] = null; }
  return _upsellProducts[handle];
}

// Match each card's variant to the bracelet's active Type (diabetes), else default.
function syncUpsellCard(card) {
  const product = _upsellProducts[card.dataset.upsellHandle];
  if (!product) return;
  const variants = product.variants || [];
  if (!variants.length) return;
  const activeType = $('.type-pill.active')?.dataset.type;
  let match = variants[0];
  if (activeType) {
    const lc = activeType.toLowerCase();
    const found = variants.find(v =>
      (v.options || []).some(o => String(o).toLowerCase() === lc) ||
      (v.title || '').toLowerCase().includes(lc)
    );
    if (found) match = found;
  }
  const sw = card.querySelector('[data-upsell-switch]');
  if (sw) {
    sw.dataset.currentVariant = match.id;
    if (match.title) sw.setAttribute('aria-label', `Add ${product.title} (${match.title}) to your order`);
  }
  const variantLabel = card.querySelector('[data-upsell-variant-label]');
  if (variantLabel) {
    const label = (match.title && match.title !== 'Default Title') ? match.title.replace(/\s*\/\s*/g, ' · ') : '';
    variantLabel.textContent = label;
    variantLabel.style.display = label ? '' : 'none';
  }
  if (match.featured_image && match.featured_image.src) {
    const imgEl = card.querySelector('[data-upsell-image]');
    if (imgEl) { imgEl.src = match.featured_image.src; imgEl.dataset.fullImage = match.featured_image.src; }
  }
  // Refresh price/compare/Save % from the live per-market probe for the matched
  // variant (Liquid only rendered variants.first; the selected Type can differ).
  updateUpsellCardPrice(card);
}

// Show the add-on at its live sale price with a struck-through "was" compare-at
// and a "Save X%" pill, all in the buyer's market currency. Pulls the true
// per-market price + compare-at from the section probe (money-converted,
// Markets-override-aware) rather than base-currency /products/x.js.
async function updateUpsellCardPrice(card) {
  const handle = card.dataset.upsellHandle;
  const sw = card.querySelector('[data-upsell-switch]');
  const variantId = sw ? parseInt(sw.dataset.currentVariant, 10) : NaN;
  if (!handle || !variantId) return;
  const mp = await fetchMarketPrices(handle);
  const v = mp && mp.get(variantId);
  if (!v) return;
  const priceEl = card.querySelector('[data-upsell-price]');
  const compareEl = card.querySelector('[data-upsell-compare]');
  const saveEl = card.querySelector('[data-upsell-save]');
  if (priceEl) priceEl.textContent = fmt(v.price);
  const onSale = v.compare && v.compare > v.price;
  const pct = onSale ? Math.round((v.compare - v.price) / v.compare * 100) : 0;
  if (compareEl) {
    compareEl.textContent = onSale ? fmt(v.compare) : '';
    compareEl.hidden = !onSale;
  }
  if (saveEl) {
    if (onSale && pct > 0) { saveEl.textContent = `Save ${pct}%`; saveEl.hidden = false; }
    else { saveEl.hidden = true; }
  }
  updateMainCtaText();
}

function syncAllUpsells() {
  $$('[data-upsell-card]').forEach(syncUpsellCard);
  updateMainCtaText();
}

function getCurrentBundlePriceCents() {
  const card = $('.bundle-card.active');
  if (!card) return 0;
  const major = parseFloat(card.dataset.price);
  if (isNaN(major)) return 0;
  return Math.round(major * 100);
}

function updateMainCtaText() {
  // On BOGO pages there are no bundle cards; bogo.js owns both the main CTA and
  // the shared floating bar. Bailing out prevents this bundle logic from
  // clobbering the shared #floatingPrice with a £0 (no active bundle) value.
  if (document.querySelector('[data-bogo]')) return;
  const bundleCard = $('.bundle-card.active');
  const bundleDisplay = bundleCard?.dataset.displayPrice || '';
  // The active bundle card's data-price is the DISCOUNTED bundle total.
  let totalCents = getCurrentBundlePriceCents();
  // Add each toggled-on add-on using the price SHOWN on its card (Shopify's
  // converted presentment value — raw variant.price / .js are base currency).
  let toggledCount = 0;
  $$('[data-upsell-card]').forEach((card) => {
    const sw = card.querySelector('[data-upsell-switch]');
    if (sw && sw.getAttribute('aria-checked') === 'true') {
      toggledCount++;
      const priceEl = card.querySelector('[data-upsell-price]');
      totalCents += priceEl ? parsePriceToCents(priceEl.textContent) : 0;
    }
  });
  const priceText = (toggledCount === 0 && bundleDisplay) ? bundleDisplay : fmt(totalCents);
  // Label stays "Add to cart" regardless of quantity; only the price updates.
  const labelText = 'Add to cart';
  const mainLabel = $('#mainCtaLabel');
  const mainPrice = $('#mainCtaPrice');
  const floatLabel = $('#floatingLabel');
  const floatPrice = $('#floatingPrice');
  if (mainLabel) mainLabel.textContent = labelText;
  if (mainPrice) mainPrice.textContent = priceText;
  if (floatLabel) floatLabel.textContent = labelText;
  if (floatPrice) floatPrice.textContent = priceText;
}

// Shared image lightbox
function openUpsellImageModal(trigger) {
  const modal = $('[data-upsell-image-modal]');
  const modalImg = modal?.querySelector('[data-upsell-modal-img]');
  if (!modal || !modalImg || !trigger) return;
  modalImg.src = trigger.dataset.fullImage || trigger.src;
  modal.dataset.open = 'true';
  modal.setAttribute('aria-hidden', 'false');
  document.body.style.overflow = 'hidden';
}
function closeUpsellImageModal() {
  const modal = $('[data-upsell-image-modal]');
  if (!modal) return;
  modal.dataset.open = 'false';
  modal.setAttribute('aria-hidden', 'true');
  document.body.style.overflow = '';
}

function initUpsellCarousel() {
  const carousel = $('[data-upsell-carousel]');
  if (!carousel) return;
  const track = carousel.querySelector('[data-upsell-track]');
  const slides = Array.from(carousel.querySelectorAll('.upsell-carousel__slide'));
  const dotsWrap = carousel.querySelector('[data-upsell-dots]');
  const prevBtn = carousel.querySelector('[data-upsell-prev]');
  const nextBtn = carousel.querySelector('[data-upsell-next]');
  const nav = carousel.querySelector('.upsell-carousel__nav');
  // After dead cards are dropped at runtime, the carousel may be left with 0 or 1 real
  // slide — hide the whole thing (0) or just the dots/arrows nav (1) so nothing breaks.
  if (slides.length === 0) { carousel.style.display = 'none'; return; }
  if (!track || slides.length <= 1) { if (nav) nav.style.display = 'none'; carousel.setAttribute('data-single', ''); return; }

  if (dotsWrap) {
    slides.forEach((_, i) => {
      const d = document.createElement('button');
      d.type = 'button';
      d.className = 'upsell-dot';
      d.setAttribute('aria-label', `Go to add-on ${i + 1}`);
      d.addEventListener('click', () => scrollToIndex(i));
      dotsWrap.appendChild(d);
    });
  }
  function currentIndex() {
    let best = 0, bestDist = Infinity;
    slides.forEach((s, i) => {
      const dist = Math.abs(s.offsetLeft - track.scrollLeft);
      if (dist < bestDist) { bestDist = dist; best = i; }
    });
    return best;
  }
  function scrollToIndex(i) {
    const s = slides[Math.max(0, Math.min(i, slides.length - 1))];
    if (s) track.scrollTo({ left: s.offsetLeft, behavior: 'smooth' });
  }
  function update() {
    const idx = currentIndex();
    if (dotsWrap) dotsWrap.querySelectorAll('.upsell-dot').forEach((d, i) => d.classList.toggle('is-active', i === idx));
    if (prevBtn) prevBtn.disabled = idx <= 0;
    if (nextBtn) nextBtn.disabled = idx >= slides.length - 1;
  }
  if (prevBtn) prevBtn.addEventListener('click', () => scrollToIndex(currentIndex() - 1));
  if (nextBtn) nextBtn.addEventListener('click', () => scrollToIndex(currentIndex() + 1));
  let raf;
  track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); });
  update();
}

(function initUpsells() {
  const cards = $$('[data-upsell-card]');
  if (!cards.length) return;

  cards.forEach((card) => {
    const sw = card.querySelector('[data-upsell-switch]');
    const imgEl = card.querySelector('[data-upsell-image]');
    const toggle = () => {
      if (!sw) return;
      sw.setAttribute('aria-checked', sw.getAttribute('aria-checked') === 'true' ? 'false' : 'true');
      updateMainCtaText();
      // Notify the BOGO CTA (bogo.js) which also reads these checkboxes.
      document.dispatchEvent(new CustomEvent('upsell:changed'));
    };
    // Whole card toggles the tick box; clicking the image opens the lightbox instead.
    card.addEventListener('click', (e) => {
      if (imgEl && (e.target === imgEl || imgEl.contains(e.target))) return;
      toggle();
    });
    if (imgEl) imgEl.addEventListener('click', (e) => { e.stopPropagation(); openUpsellImageModal(imgEl); });
    if (sw) sw.addEventListener('keydown', (e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); toggle(); } });
  });

  // Re-sync each card's variant when the bracelet Type changes
  $$('.type-pill').forEach(btn => btn.addEventListener('click', () => setTimeout(() => {
    syncAllUpsells();
    if (typeof syncSlotVariants === 'function') syncSlotVariants();
    updateMainCtaText();
  }, 0)));
  // Re-sync price when bundle (1/2/3 pack) changes
  $$('.bundle-card').forEach(c => c.addEventListener('click', () => setTimeout(updateMainCtaText, 0)));

  // Modal close handlers
  document.querySelectorAll('[data-modal-close]').forEach(el => el.addEventListener('click', closeUpsellImageModal));
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeUpsellImageModal(); });

  const loadAll = () => Promise.all(cards.map(async (card) => {
    const product = await loadUpsellCardProduct(card);
    // Product didn't resolve (e.g. draft/unpublished → /products/x.js 404) or has no
    // sellable variant: drop the card so no empty/broken upsell ever shows.
    if (!product || !product.handle || !(product.variants || []).length) {
      (card.closest('.upsell-carousel__slide') || card).remove();
      return null;
    }
    return product;
  })).then(() => {
    syncAllUpsells();
    initUpsellCarousel();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', loadAll);
  else loadAll();
})();

// ---------- Cart drawer ----------

const drawer = $('#drawer');
const overlay = $('#drawerOverlay');

async function openDrawer() {
  if (!drawer || !overlay) return;
  drawer.classList.add('open');
  overlay.classList.add('open');
  document.body.style.overflow = 'hidden';
  // Start the urgency countdown whenever the drawer opens with items — covers
  // every add path, including the BOGO pages (which add via bogo.js, not addCurrentToCart).
  // renderDrawer already fetched the cart, so reuse it instead of a second /cart.js call.
  const cart = await renderDrawer();
  if (cart && cart.item_count > 0) startUrgencyTimer();
}
function closeDrawer() {
  if (!drawer || !overlay) return;
  drawer.classList.remove('open');
  overlay.classList.remove('open');
  document.body.style.overflow = '';
}
$('#cartBtn')?.addEventListener('click', openDrawer);
$('#drawerClose')?.addEventListener('click', closeDrawer);
overlay?.addEventListener('click', closeDrawer);

async function renderDrawer() {
  const body = $('#drawerBody');
  const foot = $('#drawerFoot');
  if (!body || !foot) return;
  const cart = await fetchCart();
  // renderDrawer already has the fresh cart — update the count badge here so
  // callers don't need a second /cart.js fetch via updateCartCount().
  setCartCount(cart);

  if (cart.item_count === 0) {
    body.innerHTML = '<div class="py-12 text-center text-sm text-ink-muted">Your cart is empty.</div>';
    foot.hidden = true;
    const upsells = $('#drawerUpsells');
    if (upsells) upsells.hidden = true;
    return cart;
  }

  // Products whose link should NOT be clickable in the cart (e.g. free add-ons)
  const NON_CLICKABLE_PRODUCT_IDS = new Set([16280472060249]);

  // Shopify's Buy-X-Get-Y discount fragments a single product into several cart
  // lines (paid chunks + a £0 "free" chunk). Recombine each variant's PAID chunks
  // into one row, and keep the FREE units on their own clearly-labelled row.
  const isFreeLine = (it) => it.final_line_price === 0 && it.original_line_price > 0;
  const groupsMap = new Map();
  let order = 0;
  cart.items.forEach(it => {
    let g = groupsMap.get(it.variant_id);
    if (!g) { g = { s: it, order: order++, paidQty: 0, paidOrig: 0, paidFinal: 0, freeQty: 0, freeOrig: 0 }; groupsMap.set(it.variant_id, g); }
    if (isFreeLine(it)) { g.freeQty += it.quantity; g.freeOrig += it.original_line_price; }
    else { g.paidQty += it.quantity; g.paidOrig += it.original_line_price; g.paidFinal += it.final_line_price; }
  });
  const groups = [...groupsMap.values()].sort((a, b) => a.order - b.order);

  // Live per-market compare-at ("was") price for every cart line. cart.js omits
  // compare_at_price, so pull it per handle from the section probe (which renders
  // in the buyer's market, so it reflects admin + Markets overrides live). Map is
  // variant_id -> unit compare cents.
  const lineCompare = new Map();
  await Promise.all([...new Set(cart.items.map(i => i.handle))].map(async handle => {
    const mp = await fetchMarketPrices(handle);
    if (mp) mp.forEach((v, vid) => { if (v.compare) lineCompare.set(vid, v.compare); });
  }));
  // Care Kit is added at £0 so its compare-at is its whole value; fall back to the
  // hardcoded map only if the probe couldn't be reached.
  const careKitItem = cart.items.find(i => i.product_id === CARE_KIT_PRODUCT_ID);
  const careKitUnitCompare = (careKitItem && lineCompare.get(careKitItem.variant_id)) || CARE_KIT_VALUES[CART_CURRENCY] || 0;

  const star = '<svg width="11" height="11" viewBox="0 0 24 24" fill="#FBBF24" class="flex-shrink-0"><polygon points="12,2 15,9 22,9 17,14 19,21 12,17 5,21 7,14 2,9 9,9"/></svg>';

  function rowHtml(g, isFreeRow, isLast) {
    const item = g.s;
    const clickable = !NON_CLICKABLE_PRODUCT_IDS.has(item.product_id);
    const qty = isFreeRow ? g.freeQty : g.paidQty;
    let orig = isFreeRow ? g.freeOrig : g.paidOrig;
    const final = isFreeRow ? 0 : g.paidFinal;
    // Surface the per-market compare-at as a struck-through "was" price.
    // Care Kit is the £0 free line (value = its whole compare-at); every other
    // line strikes its compare-at when it's higher than what's actually charged.
    if (final === 0 && item.product_id === CARE_KIT_PRODUCT_ID && careKitUnitCompare > 0) {
      orig = careKitUnitCompare * qty;
    } else if (!isFreeRow) {
      const cmp = (lineCompare.get(item.variant_id) || 0) * qty;
      if (cmp > orig) orig = cmp;
    }
    const imgInner = item.image ? `<img src="${item.image}" alt="" class="w-20 h-20 object-cover rounded-md" />` : '';
    const img = item.image
      ? (clickable ? `<a href="${item.url}" class="flex-shrink-0">${imgInner}</a>` : `<div class="flex-shrink-0">${imgInner}</div>`)
      : '';
    const titleHtml = clickable
      ? `<a href="${item.url}" class="font-display text-base leading-tight flex-1 min-w-0 hover:underline">${item.product_title}</a>`
      : `<span class="font-display text-base leading-tight flex-1 min-w-0">${item.product_title}</span>`;
    const meta = item.variant_title ? `<p class="text-[11px] text-ink-muted mt-1">${item.variant_title}</p>` : '';
    const eyebrow = isFreeRow ? `<p class="text-[11px] uppercase tracking-[0.16em] text-emerald-700 mt-1 font-semibold">Free with your order</p>` : '';
    // Both rows get a working stepper + remove; all buttons act on the product's
    // TOTAL quantity (the offer decides which units are free), so the paid/free
    // split can shift as you change it.
    const incBtn = clickable
      ? `<button data-var-inc="${item.variant_id}" class="px-3 py-1">+</button>`
      : `<button class="px-3 py-1 text-ink-faint cursor-not-allowed" disabled aria-disabled="true">+</button>`;
    const controls = `<div class="inline-flex items-center border border-line rounded-full overflow-hidden text-sm">
          <button data-var-dec="${item.variant_id}" class="px-3 py-1">−</button>
          <span class="px-2">${qty}</span>
          ${incBtn}
        </div>`;
    const removeHtml = `<button data-var-remove="${item.variant_id}" aria-label="Remove" class="text-ink-faint hover:text-ink">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="6" y1="18" x2="18" y2="6"/></svg>
            </button>`;
    const trustHtml = clickable ? `
          <div class="mt-2 flex flex-col gap-1 text-[11px] text-ink-muted">
            <div class="flex items-center gap-1.5">
              <span class="flex items-center gap-0.5" aria-label="4.8 out of 5 stars">${star.repeat(5)}</span>
              <span>4.8 / 5 rated</span>
            </div>
            <div class="flex items-center gap-1.5">
              <span class="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 flex-shrink-0">
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20,6 9,17 4,12"/></svg>
              </span>
              <span>In stock. Ready to dispatch.</span>
            </div>
          </div>` : '';
    const itemBorder = isLast ? '' : 'border-b border-line/60';
    return `
      <div class="flex gap-4 py-5 ${itemBorder}">
        ${img}
        <div class="flex-1 min-w-0 flex flex-col">
          <div class="flex items-start justify-between gap-3">
            ${titleHtml}
            ${removeHtml}
          </div>
          ${meta}
          ${eyebrow}
          ${trustHtml}
          <div class="flex items-center justify-between mt-3 gap-3">
            ${controls}
            <div class="flex items-baseline gap-2">
              <span class="font-display text-base${final === 0 ? ' text-emerald-700' : ''}">${final === 0 ? 'Free' : fmt(final)}</span>
              ${orig > final ? `<span class="font-display text-base text-ink-muted line-through">${fmt(orig)}</span>` : ''}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  const rows = [];
  groups.forEach(g => {
    if (g.paidQty > 0) rows.push({ g, free: false });
    if (g.freeQty > 0) rows.push({ g, free: true });
  });
  body.innerHTML = rows.map((r, i) => rowHtml(r.g, r.free, i === rows.length - 1)).join('');

  // Line keys for a variant. paidOnly=true → only the paid chunks (final > 0 or a
  // genuine £0 product); paidOnly=false → every line of the variant.
  const variantLines = (vid, paidOnly) => cart.items
    .filter(it => it.variant_id === vid && (!paidOnly || !isFreeLine(it)))
    .map(it => ({ key: it.key, quantity: it.quantity }));
  const changeLine = (key, quantity) => fetch('/cart/change.js', {
    method: 'POST', credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ id: key, quantity }),
  });

  body.querySelectorAll('[data-var-inc]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const vid = parseInt(btn.dataset.varInc, 10);
      await fetch('/cart/add.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: vid, quantity: 1 }] }),
      });
      await renderDrawer();
      await updateCartCount();
    });
  });
  body.querySelectorAll('[data-var-dec]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const vid = parseInt(btn.dataset.varDec, 10);
      // Remove one PAID unit (fall back to any line if the variant is all-free).
      const target = variantLines(vid, true)[0] || variantLines(vid, false)[0];
      if (target) await changeLine(target.key, target.quantity - 1);
      await renderDrawer();
      await updateCartCount();
    });
  });
  body.querySelectorAll('[data-var-remove]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const vid = parseInt(btn.dataset.varRemove, 10);
      // Remove every line of this variant (paid + free) by key.
      for (const l of variantLines(vid, false)) {
        try { await changeLine(l.key, 0); } catch (e) {}
      }
      await renderDrawer();
      await updateCartCount();
    });
  });

  await renderUpsells(cart);

  foot.hidden = false;
  $('#drawerTotal').textContent = fmt(cart.total_price);

  // Reference "was" subtotal + total savings shown beside the subtotal. Combines
  // BOTH sources: per-line compare-at prices (marketing "was" prices, which
  // Shopify does NOT put in total_discount) AND real cart discounts like BOGO
  // (where the free line's original_price is its value but final_price is £0).
  // For each unit, the "was" price is its compare-at when higher, else its
  // pre-discount original_price; savings = that total minus what's actually paid.
  let wasSubtotal = 0;
  cart.items.forEach(it => {
    // The free Care Kit is a gift, not a discount — never count its value in the
    // "Savings" total (it would otherwise inflate savings by its whole compare-at).
    // Counting only its £0 final price keeps its net contribution to savings zero.
    if (it.product_id === CARE_KIT_PRODUCT_ID) {
      wasSubtotal += it.final_price * it.quantity;
      return;
    }
    const cmp = lineCompare.get(it.variant_id);
    const wasUnit = (cmp && cmp > it.final_price) ? cmp : it.original_price;
    wasSubtotal += wasUnit * it.quantity;
  });
  const refSavings = wasSubtotal - cart.total_price;

  const compareEl = $('#drawerCompare');
  if (compareEl) {
    if (refSavings > 0) {
      compareEl.textContent = fmt(wasSubtotal);
      compareEl.classList.remove('hidden');
    } else {
      compareEl.textContent = '';
      compareEl.classList.add('hidden');
    }
  }

  const savingsRow = $('#drawerSavingsRow');
  if (refSavings > 0) {
    savingsRow.classList.remove('hidden');
    savingsRow.classList.add('flex');
    $('#drawerSavings').textContent = '−' + fmt(refSavings);
  } else {
    savingsRow.classList.add('hidden');
    savingsRow.classList.remove('flex');
  }

  const offer = $('#drawerOffer');
  if (offer) {
    const b = braceletUnitsInCart(cart);
    // "1 more bracelet" vs "N more bracelets".
    const plural = (n) => `${n} more bracelet${n === 1 ? '' : 's'}`;

    // Bracelet bundle: 2+ bracelets = 35% off. One milestone on the bar at 100% (2 bracelets).
    let head;
    if (b >= 2) {
      head = `<span class="cart-offer__tier">✓ 35% off unlocked</span>`;
    } else {
      head = `Add ${plural(2 - b)} to unlock <span class="cart-offer__tier">35% off</span>`;
    }
    const pct = b >= 2 ? 100 : b * 50;
    const t1Done = b >= 2;
    offer.innerHTML = `
      <p class="cart-offer__head">${head}</p>
      <div class="cart-offer__track">
        <div class="cart-offer__fill" style="width:${pct}%"></div>
        <div class="cart-offer__ms${t1Done ? ' is-done' : ''}" style="left:100%"></div>
        <div class="cart-offer__thumb" style="left:${pct}%"></div>
      </div>
      <div class="cart-offer__labels">
        <span class="${t1Done ? 'is-done' : ''}" style="right:0">${t1Done ? '✓ ' : ''}35% off</span>
      </div>`;

    // Clear any leftover rotation timers from the previous bar design.
    if (window._cartMsgTimer) { clearInterval(window._cartMsgTimer); window._cartMsgTimer = null; }
    if (window._cartMsgReset) { clearTimeout(window._cartMsgReset); window._cartMsgReset = null; }
  }

  // Align the drawer close-X to the line-item remove-X column at runtime
  alignDrawerCloseToRemoveX();
  return cart;
}

// Measure where the cart-item remove-X actually sits and pin the drawer
// close-X to the same vertical column. Robust to scrollbar width, padding
// quirks, and SVG centring differences between the 22px close and 14px remove icons.
function alignDrawerCloseToRemoveX() {
  const close = document.getElementById('drawerClose');
  if (!close) return;
  // The serif title sits low within its line box, so centre-aligning the icon to
  // the flex row leaves the X visually high. Nudge it down by a fraction of the
  // title's font-size so it lands on the text's optical centre and scales across
  // mobile (1.25rem) and desktop (1.5rem).
  const title = document.querySelector('.drawer__title');
  const fs = title ? (parseFloat(getComputedStyle(title).fontSize) || 20) : 20;
  const dy = +(fs * 0.12).toFixed(1);

  const removeBtn = document.querySelector('#drawerBody [data-remove]');
  if (!removeBtn) {
    // Empty cart fallback — vertical centring only, no horizontal shift.
    close.style.transform = `translateY(${dy}px)`;
    return;
  }
  // Reset first so the horizontal measurement isn't skewed by a prior transform,
  // then wait one frame for layout to settle before reading rects.
  close.style.transform = 'none';
  requestAnimationFrame(() => {
    const closeRect = close.getBoundingClientRect();
    const removeRect = removeBtn.getBoundingClientRect();
    const closeCenter = closeRect.left + closeRect.width / 2;
    const removeCenter = removeRect.left + removeRect.width / 2;
    const dx = Math.round(removeCenter - closeCenter);
    close.style.transform = `translate(${dx}px, ${dy}px)`;
  });
}

// ---------- Upsells (cart-aware: bracelet → matching keychain) ----------

// Bracelet-bundle discount an upsell would actually earn, based on the
// RESULTING number of bracelets if it were added (bracelets already in cart + 1):
//   1 bracelet  -> no discount (a single bracelet earns nothing)
//   2+ bracelets -> 35% off (all markets)
// Keychains and the free care kit never count toward the bracelet bundle.
function braceletUnitsInCart(cart) {
  return ((cart && cart.items) || []).reduce((n, i) =>
    /bracelet/i.test(i.product_title || '') ? n + (i.quantity || 0) : n, 0);
}
// Undiscounted per-unit bracelet price in the customer's MARKET currency.
// /products/x.js prices are the shop's base currency, so for non-base markets we
// read the price off a bracelet already in the cart (cart.js prices are presentment).
function braceletMarketUnitCents(cart, fallbackCents) {
  const it = ((cart && cart.items) || []).find(i => /bracelet/i.test(i.product_title || ''));
  const cents = it ? (it.original_price || it.price) : 0;
  return cents || fallbackCents || 0;
}

// Bracelet bundle discount: the reward is a % off, applied to the whole bundle.
//   resulting 2+ bracelets -> 35% off.
// An "add another bracelet" upsell shows the price that bracelet earns at the
// tier it unlocks (bracelets already in cart + 1), with the full unit price
// struck through. Returns { price, compare, free } in market cents, or null if
// no unit price is known.
function braceletTierPct(resultingCount) {
  if (resultingCount >= 2) return 35;
  return 0;
}
function braceletUpsellPrice(cart, fallbackCents) {
  const unit = braceletMarketUnitCents(cart, fallbackCents);
  if (!unit) return null;
  const pct = braceletTierPct(braceletUnitsInCart(cart) + 1);
  if (!pct) return { price: unit, compare: null, free: false };
  const price = Math.round(unit * (100 - pct) / 100);
  return { price, compare: unit, free: false };
}

// Candidate keychain handles to try (first one that resolves wins)
const UPSELL_KEYCHAIN_HANDLES = {
  bloodThinner: ['blood-thinner-keychain', 'bloodthinner-keychain'],
  diabetes: ['diabetes-keychain'],
  eliquis: ['eliquis-keychain'],
  ckd: ['chronic-kidney-disease-ckd-keychain', 'ckd-keychain', 'kidney-disease-keychain'],
};

// Additional condition keychain (the enamel-disc variant) offered alongside the
// standard one. Shown only when that specific product isn't already in the cart.
const UPSELL_EXTRA_KEYCHAIN_HANDLES = {
  bloodThinner: 'blood-thinner-keychain-1',
  diabetes: 'diabetes-keychain-1',
  eliquis: 'eliquis-keychain-1',
  ckd: 'chronic-kidney-disease-ckd-keychain-1',
};

// All keychain styles per condition (standard + enamel-disc "-1"). A keychain in
// the cart cross-sells the OTHER keychain style(s) of the same condition,
// skipping any already in the cart.
const CONDITION_KEYCHAINS = {
  bloodThinner: ['blood-thinner-keychain', 'blood-thinner-keychain-1'],
  diabetes:     ['diabetes-keychain', 'diabetes-keychain-1'],
  eliquis:      ['eliquis-keychain', 'eliquis-keychain-1'],
  ckd:          ['chronic-kidney-disease-ckd-keychain', 'chronic-kidney-disease-ckd-keychain-1'],
};

// Universal cross-sell offered alongside ANY product (not condition-restricted).
const UNIVERSAL_UPSELL_HANDLE = 'capsule-necklace';

// Condition-matched necklaces — offered when a product of that condition is in
// the cart. Conditions without their own necklace fall back to the capsule one.
const UPSELL_NECKLACE_HANDLES = {
  diabetes: 'diabetes-necklace',
  bloodThinner: 'blood-thinner-necklace',
  eliquis: 'eliquis-necklace',
};

// Condition of any cart line (bracelet, keychain or necklace) from its title.
function detectConditionFromItem(item) {
  const title = (item.product_title || '').toLowerCase();
  if (title.includes('eliquis') || title.includes('apixaban')) return 'eliquis';
  if (title.includes('kidney') || title.includes('ckd')) return 'ckd';
  if (title.includes('blood') && title.includes('thinner')) return 'bloodThinner';
  if (title.includes('diabetes') || title.includes('diabetic')) return 'diabetes';
  return null;
}

// Cross-sell families: if the cart contains any member of a family, the other
// members (not already in cart) are offered as drawer upsells. Families are
// gender-pure on purpose — female only suggests female, male→male, unisex→unisex
// — so a women's bracelet never cross-sells the men's/unisex one and vice versa.
// Every bracelet of a condition (all styles/genders). First handle in each list
// is the "representative" used when suggesting a bracelet for a lone keychain.
const CONDITION_BRACELETS = {
  bloodThinner: { female: ['blood-thinner-womens', 'blood-thinner-slim-bracelet', 'blood-thinner-bracelet-2'], male: ['blood-thinner-bracelet', 'blood-thinner-bracelet-1'] },
  diabetes:     { female: ['diabetes-bracelet-womens', 'diabetes-slim-bracelet', 'diabetes-bracelet-2'], male: ['diabetes-bracelet', 'diabetes-bracelet-1'] },
  eliquis:      { female: ['eliquis-bracelet-womens', 'eliquis-slim-bracelet', 'eliquis-bracelet-3'], male: ['eliquis-bracelet', 'eliquis-bracelet-2'] },
  ckd:          { female: ['chronic-kidney-disease-ckd-bracelet', 'chronic-kidney-disease-ckd-slim-bracelet', 'chronic-kidney-disease-ckd-bracelet-2'], male: ['chronic-kidney-disease-ckd-bracelet-1'] },
  epilepsy:     { female: ['epilepsy-bracelet-womens'], male: ['epilepsy-bracelet'] },
  pacemaker:    { female: ['pacemaker-bracelet', 'pacemaker-slim-bracelet'], male: [] },
};

// Cross-sell families: bracelets cross-sell others of the SAME condition AND the
// SAME gender (women's with women's, men's with men's — never across genders).
// Derived from CONDITION_BRACELETS so new bracelets added there auto-join.
const CROSS_SELL_FAMILIES = [];
for (const c of Object.values(CONDITION_BRACELETS)) {
  if ((c.female || []).length > 1) CROSS_SELL_FAMILIES.push(c.female);
  if ((c.male || []).length > 1) CROSS_SELL_FAMILIES.push(c.male);
}

// Given a bracelet handle, find its condition + gender family so the cart can
// suggest same-condition, same-gender siblings (women's → women's, men's → men's).
// Returns { condition, gender, siblings } or null if the handle isn't a known bracelet.
function braceletFamilyOf(handle) {
  for (const [condition, groups] of Object.entries(CONDITION_BRACELETS)) {
    for (const gender of ['female', 'male']) {
      if ((groups[gender] || []).includes(handle)) {
        return { condition, gender, siblings: groups[gender] };
      }
    }
  }
  return null;
}

// Which condition a keychain belongs to (drives the reverse keychain→bracelet upsell).
const KEYCHAIN_CONDITION = {
  'blood-thinner-keychain': 'bloodThinner',
  'bloodthinner-keychain': 'bloodThinner',
  'diabetes-keychain': 'diabetes',
  'eliquis-keychain': 'eliquis',
  'chronic-kidney-disease-ckd-keychain': 'ckd',
  'ckd-keychain': 'ckd',
  'blood-thinner-keychain-1': 'bloodThinner',
  'diabetes-keychain-1': 'diabetes',
  'eliquis-keychain-1': 'eliquis',
  'chronic-kidney-disease-ckd-keychain-1': 'ckd',
};

// Bestsellers collection order (best-selling first) — mirrors the "bestsellers"
// collection. Drives best-seller sorting of the PDP cross-sell carousel.
const BESTSELLER_ORDER = ['eliquis-slim-bracelet', 'blood-thinner-slim-bracelet', 'diabetes-slim-bracelet', 'blood-thinner-womens', 'blood-thinner-bracelet', 'eliquis-bracelet', 'diabetes-bracelet', 'eliquis-bracelet-womens', 'diabetes-bracelet-womens', 'chronic-kidney-disease-ckd-slim-bracelet', 'eliquis-bracelet-3', 'chronic-kidney-disease-ckd-bracelet', 'blood-thinner-bracelet-2', 'blood-thinner-bracelet-1', 'diabetes-bracelet-2', 'eliquis-bracelet-2', 'diabetes-bracelet-1', 'chronic-kidney-disease-ckd-bracelet-1', 'pacemaker-slim-bracelet', 'pacemaker-bracelet', 'penicillin-allergy-bracelet', 'chronic-kidney-disease-ckd-bracelet-2', 'epilepsy-bracelet', 'epilepsy-bracelet-womens'];
const BESTSELLER_RANK = {};
BESTSELLER_ORDER.forEach((h, i) => { BESTSELLER_RANK[h] = i; });

// Products that carry the "Best Seller" badge — keep in sync with
// snippets/best-seller-badge.liquid (same handle list).
const BEST_SELLER_BADGE_HANDLES = ['eliquis-slim-bracelet', 'blood-thinner-slim-bracelet', 'diabetes-slim-bracelet', 'blood-thinner-womens', 'blood-thinner-bracelet', 'eliquis-bracelet', 'diabetes-bracelet', 'eliquis-bracelet-womens'];

const _upsellCache = {};

// Live per-market price + compare-at, pulled via the Section Rendering API so
// the values reflect the buyer's market (Liquid `money` converts + applies any
// Markets price-list overrides). /products/x.js and /cart.js can't do this:
// the former is base-currency, the latter omits compare_at_price entirely.
// Returns a Map<variantId,{price,compare}> in presentment cents (compare = null
// when there's no valid compare-at). Cached per handle; null on failure.
const _marketPriceCache = {};
const _marketPriceInflight = {}; // handle -> Promise, so concurrent callers (pre-warm + drawer) share one probe
function fetchMarketPrices(handle) {
  if (_marketPriceCache[handle] !== undefined) return Promise.resolve(_marketPriceCache[handle]);
  if (_marketPriceInflight[handle]) return _marketPriceInflight[handle];
  const p = (async () => {
    try {
      const res = await fetch(`/products/${handle}?section_id=market-price-probe`, { credentials: 'same-origin' });
      if (!res.ok) { _marketPriceCache[handle] = null; return null; }
      // ?section_id= wraps the output in <div class="shopify-section">…</div>, so
      // pull out the JSON payload (first { … last }) rather than parsing the wrapper.
      const raw = await res.text();
      const start = raw.indexOf('{'), end = raw.lastIndexOf('}');
      if (start === -1 || end === -1) { _marketPriceCache[handle] = null; return null; }
      const data = JSON.parse(raw.slice(start, end + 1));
      const map = new Map();
      (data.variants || []).forEach(v => map.set(v.id, {
        price: parsePriceToCents(v.price),
        compare: v.compare ? parsePriceToCents(v.compare) : null,
      }));
      _marketPriceCache[handle] = map;
      return map;
    } catch (e) {
      _marketPriceCache[handle] = null;
      return null;
    } finally {
      delete _marketPriceInflight[handle];
    }
  })();
  _marketPriceInflight[handle] = p;
  return p;
}

// Market-correct { price, compare } (presentment cents) for one variant, via the
// section probe. Falls back to the base-currency values from /products/x.js if the
// probe fails. compare = null when there's no valid compare-at price.
async function marketVariantPrice(handle, variant) {
  const mp = await fetchMarketPrices(handle);
  const v = mp && mp.get(variant.id);
  if (v) return { price: v.price, compare: v.compare };
  const compare = (variant.compare_at_price && variant.compare_at_price > variant.price) ? variant.compare_at_price : null;
  return { price: variant.price, compare };
}

async function fetchProduct(handle) {
  if (_upsellCache[handle] !== undefined) return _upsellCache[handle];
  try {
    const res = await fetch(`/products/${handle}.js`);
    if (!res.ok) {
      _upsellCache[handle] = null;
      return null;
    }
    const product = await res.json();
    _upsellCache[handle] = product;
    return product;
  } catch (e) {
    _upsellCache[handle] = null;
    return null;
  }
}

async function fetchProductTryHandles(handles) {
  for (const handle of handles) {
    const product = await fetchProduct(handle);
    if (product) return product;
  }
  return null;
}

function findVariantByOption(product, optionValue) {
  if (!product || !product.variants) return null;
  const target = String(optionValue).toLowerCase();
  return product.variants.find(v =>
    (v.options || []).some(o => String(o).toLowerCase() === target) ||
    (v.title || '').toLowerCase().includes(target)
  );
}

// Find the alternate-colour variant of the same product.
// Strategy: identify which option is the Colour, then find a variant that
// matches the current variant on every option EXCEPT colour (where it must differ).
function findAlternateColourVariant(product, currentVariantId) {
  if (!product || !product.variants) return null;
  const current = product.variants.find(v => v.id === currentVariantId);
  if (!current) return null;

  // product.options can be ["Colour","Type"] or [{name:"Colour"}, ...]
  const optionNames = (product.options || []).map(o =>
    typeof o === 'string' ? o : (o && o.name ? o.name : '')
  );
  let colourIdx = optionNames.findIndex(n => /colou?r/i.test(n));

  // Fallback: if no obvious colour option, just return any other available variant
  if (colourIdx === -1) {
    return product.variants.find(v => v.id !== currentVariantId && v.available !== false) || null;
  }

  const currentOptions = current.options || [];
  return product.variants.find(v => {
    if (v.id === currentVariantId) return false;
    if (v.available === false) return false;
    const opts = v.options || [];
    if (opts.length !== currentOptions.length) return false;
    for (let i = 0; i < opts.length; i++) {
      if (i === colourIdx) {
        if (opts[i] === currentOptions[i]) return false; // colour must differ
      } else {
        if (opts[i] !== currentOptions[i]) return false; // other options must match
      }
    }
    return true;
  }) || null;
}

// Detect alternate-colour upsell from a bracelet in the cart.
// Returns the handle + current variant id; the alternate is resolved against
// the fetched product so colour names don't have to be hardcoded.
function detectColourSwapFromItem(item) {
  const title = (item.product_title || '').toLowerCase();
  if (title.includes('keychain') || title.includes('key chain') || title.includes('key-chain')) return null;
  if (!title.includes('bracelet')) return null;
  if (!item.handle || !item.variant_id) return null;
  return { handle: item.handle, currentVariantId: item.variant_id };
}

// Detect whether a cart item is a bracelet that should trigger an upsell
function detectUpsellFromItem(item) {
  const title = (item.product_title || '').toLowerCase();
  const variant = (item.variant_title || '').toLowerCase();

  // Skip keychains themselves
  if (title.includes('keychain') || title.includes('key chain') || title.includes('key-chain')) return null;

  // Must be a bracelet (or product without "keychain")
  const isBracelet = title.includes('bracelet') || !title.includes('keychain');
  if (!isBracelet) return null;

  if (title.includes('eliquis') || title.includes('apixaban')) {
    return { kind: 'eliquis' };
  }

  if (title.includes('kidney') || title.includes('ckd')) {
    return { kind: 'ckd' };
  }

  if (title.includes('blood') && title.includes('thinner')) {
    return { kind: 'bloodThinner' };
  }

  if (title.includes('diabetes') || title.includes('diabetic')) {
    if (variant.includes('type 1') || variant.includes('type1') || variant.includes('t1')) {
      return { kind: 'diabetes', option: 'Type 1' };
    }
    if (variant.includes('type 2') || variant.includes('type2') || variant.includes('t2')) {
      return { kind: 'diabetes', option: 'Type 2' };
    }
    // Type unspecified — default to Type 2 (more common)
    return { kind: 'diabetes', option: 'Type 2' };
  }

  return null;
}

// Read the diabetes Type (if any) from a cart line, e.g. "Silver / Type 2 Diabetes".
// Returns the full option value so it can be matched against another product's variants.
function detectTypeOption(item) {
  const hay = `${item.variant_title || ''} ${(item.options || []).join(' ')}`.toLowerCase();
  if (hay.includes('type 1')) return 'Type 1 Diabetes';
  if (hay.includes('type 2')) return 'Type 2 Diabetes';
  return null;
}

// Index of the Colour option within a product's options (-1 if none).
function colourOptionIndex(product) {
  const optionNames = (product.options || []).map(o =>
    typeof o === 'string' ? o : (o && o.name ? o.name : '')
  );
  return optionNames.findIndex(n => /colou?r/i.test(n));
}

// Build one upsell card row. comparePrice (optional) renders a strikethrough original.
function buildUpsellCard(product, variant, displayPrice, comparePrice, freeLabel) {
  const variantImage = variant.featured_image && variant.featured_image.src
    ? variant.featured_image.src
    : product.featured_image;
  const img = variantImage
    ? `<img src="${variantImage}" alt="" loading="lazy" />`
    : '';
  const variantLabel = variant.title && variant.title !== 'Default Title'
    ? `<p class="cart-upsell-card__variant">${variant.title}</p>`
    : '';
  // Save X% pill (mirrors the PDP tier pill) — shown to the right of the compare-at
  // price on genuinely discounted upsells. Not on "Free" rows (the label already says it).
  const savePct = !freeLabel && comparePrice && comparePrice > displayPrice
    ? Math.round(((comparePrice - displayPrice) / comparePrice) * 100)
    : 0;
  const savePill = savePct > 0
    ? `<span class="cart-upsell-card__save">Save ${savePct}%</span>`
    : '';
  const priceHtml = freeLabel
    ? `<span class="cart-upsell-card__now cart-upsell-card__now--free">Free</span><span class="compare">${fmt(comparePrice)}</span>`
    : comparePrice
      ? `<span class="cart-upsell-card__now">${fmt(displayPrice)}</span><span class="compare">${fmt(comparePrice)}</span>${savePill}`
      : `<span class="cart-upsell-card__now">${fmt(displayPrice)}</span>`;
  // "Add & Save" when there's a real saving (sale or free), else a plain "Add".
  const onSale = freeLabel || (comparePrice && comparePrice > displayPrice);
  const btnLabel = onSale ? 'Add &amp; Save' : 'Add to Cart';
  const linkUrl = `${product.url}?variant=${variant.id}`;
  return `
      <div class="cart-upsell-card">
        <a href="${linkUrl}" class="cart-upsell-card__media">${img}</a>
        <div class="cart-upsell-card__body">
          <a href="${linkUrl}" class="cart-upsell-card__title">${product.title}</a>
          <p class="cart-upsell-card__price">${priceHtml}</p>
          ${variantLabel}
        </div>
        <button data-add-upsell="${variant.id}" class="cart-upsell-card__btn">${btnLabel}</button>
      </div>
    `;
}

// ---------- PDP cross-sell carousel ("You might also like") ----------
// Shows the current product's sibling BRACELETS (same condition + gender family,
// best-seller order) followed by the condition NECKLACE. Never keychains. Uses the
// same family data as the cart cross-sell upsells.

function pdpCrossSellCondition(product) {
  const byTitle = detectConditionFromItem({ product_title: product.title });
  if (byTitle) return byTitle;
  const h = product.handle || '';
  if (h.includes('eliquis')) return 'eliquis';
  if (h.includes('ckd') || h.includes('kidney')) return 'ckd';
  if (h.includes('blood-thinner')) return 'bloodThinner';
  if (h.includes('diabetes')) return 'diabetes';
  if (h.includes('epilepsy')) return 'epilepsy';
  if (h.includes('pacemaker')) return 'pacemaker';
  return null;
}

// Reuses the cart "Bundle & Save" card design (.cart-upsell-card) so the PDP
// cross-sell matches the in-cart upsell, plus a Best Seller badge on the image.
function buildPdpCrossSellCard(product, variant, price, compare, colourLabel) {
  const image = (variant.featured_image && variant.featured_image.src) || product.featured_image;
  const img = image ? `<img src="${image}" alt="" loading="lazy" />` : '';
  const badge = BEST_SELLER_BADGE_HANDLES.includes(product.handle)
    ? '<span class="best-seller-badge">Best Seller</span>' : '';
  const label = colourLabel || (variant.title && variant.title !== 'Default Title' ? variant.title : '');
  const variantLabel = label ? `<p class="cart-upsell-card__variant">${label}</p>` : '';
  const priceHtml = (compare && compare > price)
    ? `<span class="cart-upsell-card__now">${fmt(price)}</span><span class="compare">${fmt(compare)}</span>`
    : `<span class="cart-upsell-card__now">${fmt(price)}</span>`;
  return `
      <div class="cart-upsell-card pdp-xsell-card">
        <a href="${product.url}" class="cart-upsell-card__media">${badge}${img}</a>
        <div class="cart-upsell-card__body">
          <a href="${product.url}" class="cart-upsell-card__title">${product.title}</a>
          <p class="cart-upsell-card__price">${priceHtml}</p>
          ${variantLabel}
        </div>
        <button type="button" data-xsell-add="${variant.id}" class="cart-upsell-card__btn">Add to Cart</button>
      </div>
    `;
}

async function renderPdpCrossSell() {
  const section = $('[data-pdp-xsell]');
  const track = $('[data-pdp-xsell-track]');
  if (!section || !track) return;
  const product = getProduct();
  if (!product || !product.handle) return;

  const condition = pdpCrossSellCondition(product);
  if (!condition) return;
  const selfHandle = product.handle;
  // Mirror the Type the shopper has selected on this PDP (e.g. Type 2 Diabetes):
  // only cross-sell that Type's bracelets + necklace. null when there's no Type.
  const wantType = getSelectedOptions().Type || null;

  // Bracelets: the gender-pure family that contains this product (fall back to all
  // condition bracelets for non-bracelet PDPs, e.g. a necklace), minus itself.
  const fams = CONDITION_BRACELETS[condition];
  let braceletHandles = [];
  if (fams) {
    if ((fams.female || []).includes(selfHandle)) braceletHandles = (fams.female || []).slice();
    else if ((fams.male || []).includes(selfHandle)) braceletHandles = (fams.male || []).slice();
    else braceletHandles = [...(fams.female || []), ...(fams.male || [])];
  }
  braceletHandles = braceletHandles.filter(h => h !== selfHandle);

  // Necklace for the condition (if any and not the current product).
  const necklaceHandles = [];
  const nk = UPSELL_NECKLACE_HANDLES[condition];
  if (nk && nk !== selfHandle) necklaceHandles.push(nk);

  // Bracelets first (best-seller order), then necklaces.
  const rank = h => (BESTSELLER_RANK[h] === undefined ? 9999 : BESTSELLER_RANK[h]);
  braceletHandles.sort((a, b) => rank(a) - rank(b));
  const ordered = [...braceletHandles, ...necklaceHandles];
  if (ordered.length === 0) return;

  // One card per COLOUR variant (Silver, Gold, …), like the cart cross-sell.
  const cardGroups = await Promise.all(ordered.map(async handle => {
    const p = await fetchProduct(handle);
    if (!p) return [];
    const colourIdx = colourOptionIndex(p);
    // Keep only variants matching the PDP's selected Type (when there is one).
    const wt = wantType ? wantType.toLowerCase() : null;
    let pool = (p.variants || []);
    if (wt) {
      pool = pool.filter(v =>
        (v.options || []).some(o => String(o).toLowerCase() === wt) ||
        (v.title || '').toLowerCase().includes(wt)
      );
    }
    const seenColour = new Set();
    const out = [];
    for (const v of pool) {
      if (v.available === false) continue;
      const colourKey = (colourIdx !== -1 ? String(v.options[colourIdx]) : (v.title || '')).toLowerCase();
      if (seenColour.has(colourKey)) continue; // one card per colour
      seenColour.add(colourKey);
      const mv = await marketVariantPrice(p.handle, v);
      out.push(buildPdpCrossSellCard(p, v, mv.price, mv.compare, colourIdx !== -1 ? String(v.options[colourIdx]) : null));
    }
    return out;
  }));

  track.innerHTML = cardGroups.flat().filter(Boolean).join('');
  if (!track.innerHTML.trim()) return;
  section.hidden = false;

  track.querySelectorAll('[data-xsell-add]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      const variantId = parseInt(btn.dataset.xsellAdd, 10);
      const prevLabel = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';
      await addToCart(variantId, 1, addSourceProps('pdp-crosssell', 'product-page'));
      await openDrawer(); // opens + renders the drawer (and refreshes the count badge)
      btn.textContent = prevLabel;
      btn.disabled = false;
    });
  });
}
renderPdpCrossSell();

// ---------- Accordions: only one <details> open at a time per group ----------
// When a <details> opens, close its sibling <details> (same parent). Independent
// accordion groups stay independent; within a group only one stays open.
document.querySelectorAll('details').forEach(d => {
  d.addEventListener('toggle', () => {
    if (!d.open) return;
    const parent = d.parentElement;
    if (!parent) return;
    parent.querySelectorAll(':scope > details[open]').forEach(other => {
      if (other !== d) other.open = false;
    });
  });
});

async function renderUpsells(cart) {
  const container = $('#drawerUpsells');
  const list = $('#drawerUpsellsList');
  if (!container || !list) return;

  list.innerHTML = '';

  const cartTitlesLower = cart.items.map(i => (i.product_title || '').toLowerCase());
  const cartVariantIds = new Set(cart.items.map(i => i.variant_id));
  const cartHandles = new Set(cart.items.map(i => i.handle));
  const seen = new Set();
  const candidates = [];

  // Pass 0: BEST-SELLING bracelet siblings. For each bracelet in the cart, offer
  // up to 2 best-selling bracelets from the SAME condition + SAME gender family
  // (women's → women's, men's → men's), excluding any already in the cart. If the
  // cart holds only a keychain (no bracelet) for a condition, fall back to that
  // condition's best-selling bracelets so the buyer still sees one to pair. The
  // Type (diabetes) is matched too. Capped at 2 bracelet cards across the drawer.
  const braceletPool = new Map(); // handle -> wantType (Type option to match, or null)
  const ownHandles = []; // the added bracelet(s) themselves — their OTHER colour(s) come first
  for (const item of cart.items) {
    const fam = braceletFamilyOf(item.handle);
    if (!fam) continue;
    const wantType = detectTypeOption(item);
    // The added product itself, so its ALTERNATE colour(s) get offered (e.g. added
    // Silver → suggest Gold). The in-cart colour is filtered out later by variant id.
    if (!braceletPool.has(item.handle)) {
      braceletPool.set(item.handle, wantType);
      ownHandles.push(item.handle);
    }
    // Same condition + gender sibling products.
    for (const handle of fam.siblings) {
      if (handle === item.handle) continue;
      if (cartHandles.has(handle)) continue; // already in cart
      if (!braceletPool.has(handle)) braceletPool.set(handle, wantType);
    }
  }
  for (const item of cart.items) {
    const condition = KEYCHAIN_CONDITION[item.handle];
    if (!condition) continue;
    const fam = CONDITION_BRACELETS[condition];
    if (!fam) continue;
    const allHandles = [...(fam.female || []), ...(fam.male || [])];
    if (allHandles.some(h => cartHandles.has(h))) continue; // a bracelet already covers it
    const wantType = detectTypeOption(item); // match the keychain's Type (diabetes)
    for (const handle of allHandles) {
      if (cartHandles.has(handle)) continue;
      if (!braceletPool.has(handle)) braceletPool.set(handle, wantType);
    }
  }
  // Ordering: the added bracelet's OWN alternate colour(s) first (most relevant —
  // "get it in Gold too"), then best-selling siblings. Colour variants are expanded
  // and the 2-card cap is applied below (a card = one colour of one product), so the
  // added product available in Silver + Gold can itself fill both slots.
  const rankedBraceletHandles = [
    ...ownHandles,
    ...[...braceletPool.keys()]
      .filter(h => !ownHandles.includes(h))
      .sort((a, b) => (BESTSELLER_RANK[a] ?? 999) - (BESTSELLER_RANK[b] ?? 999)),
  ];

  // Pass 0c: keychain in cart → offer the OTHER keychain style(s) for the same
  // condition (e.g. the enamel-disc vs. steel dog-tag), skipping any already in
  // the cart. Matches the diabetes Type of the keychain when present.
  for (const item of cart.items) {
    const title = (item.product_title || '').toLowerCase();
    const isKeychain = title.includes('keychain') || title.includes('key chain') || title.includes('key-chain');
    if (!isKeychain) continue;
    const condition = detectConditionFromItem(item);
    const styles = condition && CONDITION_KEYCHAINS[condition];
    if (!styles) continue;
    const wantType = detectTypeOption(item); // match the keychain's Type (diabetes)
    for (const handle of styles) {
      if (handle === item.handle) continue;
      if (cartHandles.has(handle)) continue; // already in cart
      const key = `kcOffer::${handle}`;
      if (seen.has(key)) continue;
      seen.add(key);
      candidates.push({ kind: 'keychainExtra', handle, option: wantType });
    }
  }

  // Pass 2: matching-keychain upsells (existing behaviour)
  for (const item of cart.items) {
    const detected = detectUpsellFromItem(item);
    if (!detected) continue;
    const key = `keychain::${detected.kind}::${detected.option || ''}`;
    if (seen.has(key)) continue;
    seen.add(key);

    let keyword;
    if (detected.kind === 'bloodThinner') keyword = 'blood thinner';
    else if (detected.kind === 'eliquis') keyword = 'eliquis';
    else if (detected.kind === 'ckd') keyword = 'kidney';
    else keyword = 'diabetes';
    const alreadyInCart = cartTitlesLower.some(t => t.includes(keyword) && t.includes('keychain'));
    if (alreadyInCart) continue;

    candidates.push({ kind: 'keychain', sub: detected.kind, option: detected.option });
  }

  // Pass 2b: extra condition keychain (enamel-disc variant) — offered alongside
  // the standard keychain, only if that specific product isn't already in cart.
  for (const item of cart.items) {
    const detected = detectUpsellFromItem(item);
    if (!detected) continue;
    const handle = UPSELL_EXTRA_KEYCHAIN_HANDLES[detected.kind];
    if (!handle) continue;
    if (cartHandles.has(handle)) continue;
    const key = `kcOffer::${handle}`;
    if (seen.has(key)) continue;
    seen.add(key);
    candidates.push({ kind: 'keychainExtra', handle, option: detected.option });
  }

  // Build the bracelet cards: walk siblings in best-seller order, expanding each
  // to its available colours (Silver, Gold, …), and stop once 2 cards are filled.
  // So the #1 seller's two colours can take both slots, else one colour each of
  // the top 2 sellers. Variants already in the cart are skipped.
  const MAX_BRACELET_CARDS = 2;
  const braceletCards = [];
  for (const handle of rankedBraceletHandles) {
    if (braceletCards.length >= MAX_BRACELET_CARDS) break;
    const product = await fetchProduct(handle);
    if (!product) continue;

    let pool = product.variants || [];
    const wantType = braceletPool.get(handle);
    if (wantType) {
      const lc = wantType.toLowerCase();
      const typed = pool.filter(v =>
        (v.options || []).some(o => String(o).toLowerCase() === lc) ||
        (v.title || '').toLowerCase().includes(lc)
      );
      if (typed.length) pool = typed; else continue; // no matching Type → skip product
    }

    const colourIdx = colourOptionIndex(product);
    const seenColour = new Set();
    for (const v of pool) {
      if (braceletCards.length >= MAX_BRACELET_CARDS) break;
      if (v.available === false) continue;
      if (cartVariantIds.has(v.id)) continue; // already in cart
      const colourKey = (colourIdx !== -1 ? String(v.options[colourIdx]) : (v.title || '')).toLowerCase();
      if (seenColour.has(colourKey)) continue; // one card per colour
      seenColour.add(colourKey);
      // Match the line item exactly: real per-market price + compare-at (RRP) from
      // the probe, with the bundle-tier discount this bracelet unlocks applied to it.
      const mv = await marketVariantPrice(product.handle, v);
      const upPct = braceletTierPct(braceletUnitsInCart(cart) + 1);
      const upPrice = upPct ? Math.round(mv.price * (100 - upPct) / 100) : mv.price;
      const upCompare = mv.compare || (upPct ? mv.price : null);
      braceletCards.push(buildUpsellCard(product, v, upPrice, upCompare, false));
    }
  }

  if (candidates.length === 0 && braceletCards.length === 0) {
    container.hidden = true;
    return;
  }

  const cards = await Promise.all(candidates.map(async candidate => {
    if (candidate.kind === 'keychainExtra') {
      const product = await fetchProduct(candidate.handle);
      if (!product) return '';
      const variant = candidate.option ? findVariantByOption(product, candidate.option) : product.variants[0];
      if (!variant) return '';
      const mv = await marketVariantPrice(product.handle, variant);
      return buildUpsellCard(product, variant, mv.price, mv.compare);
    }

    // Matching-keychain upsell
    const handles = UPSELL_KEYCHAIN_HANDLES[candidate.sub];
    if (!handles) return '';
    const product = await fetchProductTryHandles(handles);
    if (!product) return '';
    const variant = candidate.option ? findVariantByOption(product, candidate.option) : product.variants[0];
    if (!variant) return '';
    const mv = await marketVariantPrice(product.handle, variant);
    return buildUpsellCard(product, variant, mv.price, mv.compare);
  }));

  // Bracelets first (best-sellers), then keychains.
  list.innerHTML = [...braceletCards, ...cards].filter(Boolean).join('');
  container.hidden = list.innerHTML.trim() === '';

  list.querySelectorAll('[data-add-upsell]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      const variantId = parseInt(btn.dataset.addUpsell, 10);
      btn.disabled = true;
      btn.textContent = 'Adding…';
      await addToCart(variantId, 1, addSourceProps('cart-upsell', 'cart'));
      await renderDrawer(); // also refreshes the header count badge (setCartCount)
    });
  });
}

// ---------- Urgency countdown timer ----------

let urgencyInterval = null;

function startUrgencyTimer() {
  if (!localStorage.getItem(URGENCY_KEY)) {
    localStorage.setItem(URGENCY_KEY, String(Date.now()));
  }
  if (urgencyInterval) return;
  tickUrgency();
  urgencyInterval = setInterval(tickUrgency, 1000);
}

function stopUrgencyTimer() {
  if (urgencyInterval) clearInterval(urgencyInterval);
  urgencyInterval = null;
  localStorage.removeItem(URGENCY_KEY);
}

function tickUrgency() {
  const start = parseInt(localStorage.getItem(URGENCY_KEY), 10);
  if (!start) return;
  const remaining = URGENCY_DURATION_MS - (Date.now() - start);
  const display = $('#drawerUrgencyTimer');
  if (!display) return;
  if (remaining <= 0) {
    // Restart so the urgency feels live
    localStorage.setItem(URGENCY_KEY, String(Date.now()));
    return;
  }
  const mins = Math.floor(remaining / 60000);
  const secs = Math.floor((remaining % 60000) / 1000);
  display.textContent = `${mins}:${String(secs).padStart(2, '0')}`;
}

// On load, resume timer if cart already has items
window.addEventListener('DOMContentLoaded', async () => {
  await updateCartCount();
  const cart = await fetchCart();
  if (cart.item_count > 0 && localStorage.getItem(URGENCY_KEY)) {
    startUrgencyTimer();
  }
});

// ---------- Topbar show/hide on scroll direction ----------

(function initTopbarScroll() {
  const topbar = document.getElementById('siteTopbar');
  if (!topbar) return;
  const mobileMenuEl = document.getElementById('mobileMenu');
  const TOP_THRESHOLD = 80;
  const DELTA = 5;
  let lastY = window.scrollY;

  const setTopbarHeight = () => {
    document.documentElement.style.setProperty('--topbar-h', topbar.offsetHeight + 'px');
  };
  setTopbarHeight();
  window.addEventListener('resize', setTopbarHeight, { passive: true });
  if (window.ResizeObserver) {
    new ResizeObserver(setTopbarHeight).observe(topbar);
  }

  // On lg+ keep the header visible while the user is still inside the
  // first PDP section (above the "Why a bracelet" / #why anchor). Once
  // they scroll past it, smart hide-on-scroll resumes.
  const inFirstPdpSection = () => {
    if (window.innerWidth < 1024) return false;
    const why = document.getElementById('why');
    if (!why) return false;
    return window.scrollY < why.getBoundingClientRect().top + window.scrollY - 1;
  };

  const onScroll = () => {
    const y = window.scrollY;
    const menuOpen = mobileMenuEl && mobileMenuEl.classList.contains('open');
    if (inFirstPdpSection()) {
      topbar.classList.remove('is-hidden');
      lastY = y;
      return;
    }
    if (menuOpen || y <= TOP_THRESHOLD) {
      topbar.classList.remove('is-hidden');
      lastY = y;
    } else if (y > lastY + DELTA) {
      topbar.classList.add('is-hidden');
      lastY = y;
    } else if (y < lastY - DELTA) {
      topbar.classList.remove('is-hidden');
      lastY = y;
    }
  };

  window.addEventListener('scroll', onScroll, { passive: true });
})();

// ---------- Floating CTA show/hide based on scroll position ----------

const floatingCta = $('#floatingCta');
// Trigger off the inline Add to cart button (every PDP has #mainAddToCart);
// #shop kept as a fallback for any legacy section that still uses it.
const floatingAnchor = $('#shop') || $('#mainAddToCart') || $('#bogoAddToCart');
if (floatingCta && floatingAnchor) {
  const checkFloating = () => {
    const rect = floatingAnchor.getBoundingClientRect();
    const inView = rect.bottom > 0 && rect.top < window.innerHeight;
    // Show whenever the real Add to cart button is NOT on screen — including on
    // first load when it's still below the fold — and hide (locking into the
    // page) only while the real button is actually in view.
    floatingCta.classList.toggle('show', !inView);
  };
  window.addEventListener('scroll', checkFloating, { passive: true });
  window.addEventListener('resize', checkFloating);
  window.addEventListener('DOMContentLoaded', checkFloating);
  checkFloating(); // run now in case the script loads after DOMContentLoaded
}

// ---------- Announcement bar message rotator (slide up) ----------
(function initAnnouncementRotator() {
  document.querySelectorAll('[data-announcement]').forEach(container => {
    const msgs = container.querySelectorAll('.announcement-msg');
    if (msgs.length < 2) return;
    let i = 0;
    setInterval(() => {
      const cur = msgs[i];
      i = (i + 1) % msgs.length;
      const next = msgs[i];
      cur.classList.remove('is-active');
      cur.classList.add('is-leaving');
      next.classList.remove('is-leaving');
      next.classList.add('is-active');
      // After the slide-out finishes, reset the outgoing message back below
      // the visible area without animating, so it's ready to slide up next time.
      setTimeout(() => {
        cur.classList.add('no-anim');
        cur.classList.remove('is-leaving');
        void cur.offsetHeight; // force reflow
        cur.classList.remove('no-anim');
      }, 920);
    }, 8000);
  });
})();
